(function () {
  // ── Debug flag — set to true locally only; never commit as true ────────────
  const DEBUG = false;
  const dbg   = DEBUG ? console.log.bind(console, '[MED7]') : function () {};

  const LOCCODE_KEY  = 'med7_loccode';
  const LOCATION_KEY = 'med7_location';   // written by geolocation-setter.liquid
  const API_KEY      = 'demo-secret';
  const API_URL      = 'https://api.med7online.com/api/v1/vinculum/location';
  const MAX_LOCATION_AGE_MS = 24 * 60 * 60 * 1000; // med7_location/med7_loccode trusted for 24h before re-resolving

  const DELIVERY_CHOICE_KEY = 'med7_delivery_choice';
  // Distinguishes a customer-clicked delivery-method radio from a value that merely got
  // force-set by applyNoEligibleStores() because a location had no coverage at all. Only
  // med7SetDeliveryChoice() (the cart/mini-cart radio's own click handler) marks this —
  // no other writer of DELIVERY_CHOICE_KEY does. See recalculateDeliveryChoice.
  const DELIVERY_CHOICE_EXPLICIT_KEY = 'med7_delivery_choice_explicit';

  let cachedLoccodes = [];

  // SKUs the live Vinculum API has actually confirmed data-inventory for (PDP/Quick
  // View message only) — the message never renders a metafield-only quantity; until
  // a badge's SKU is in this set its message shows the "checking availability"
  // placeholder. Keyed by SKU (not a single scalar) so a PDP message and a Quick View
  // message for a different product can be confirmed/pending independently at once.
  const confirmedExpressSkus = new Set();

  // Cart/mini-cart equivalent of confirmedExpressSkus, keyed by variant_id (a cart can
  // hold several different SKUs at once, each independently confirmed or not).
  const confirmedExpressLineVids = new Set();

  // Builds the localized "Only N units available for 45 minute delivery..." copy,
  // sourced from window.med7ExpressDeliveryStrings (rendered by layout/theme.liquid
  // and layout/swish.theme.liquid from locales/*.json), shared by the PDP static
  // message, the PDP quantity tooltip, and the cart/mini-cart quantity tooltip.
  function buildQtyMaxTooltip(maxQty) {
    var tpl = (window.med7ExpressDeliveryStrings && window.med7ExpressDeliveryStrings.qtyTooltip) ||
      'Only [count] units is available for 45 minute delivery. Switch to Next Day delivery to add more.';
    return tpl.replace('[count]', maxQty);
  }
  window.med7BuildQtyMaxTooltip = buildQtyMaxTooltip;

  // Placeholder shown in the PDP/Quick-View express-delivery message while waiting on
  // the live API — see confirmedExpressSkus above.
  function getPDPCheckingText() {
    return (window.med7ExpressDeliveryStrings && window.med7ExpressDeliveryStrings.checkingAvailability) ||
      'Checking availability...';
  }

  // ── Location API response parsing ─────────────────────────────────────────
  // Shared by every fetch(API_URL, ...) call below. Only a non-2xx HTTP response
  // is treated as a failure (exactly the condition each call already threw on
  // before this existed) — a 200 response with an empty/no-coverage result is
  // NOT an error and is handled entirely by each caller's existing success-path
  // logic (e.g. applyNoEligibleStores()), unaffected by this. On failure, the
  // JSON error body (e.g. {"success":false,"message":"Vinculum: No Data Found."})
  // is parsed so its "message" can be surfaced in the error toast below; the
  // isLocationApiError tag lets each .catch() distinguish a genuine Location API
  // failure from an unrelated error later in the same promise chain (e.g. a
  // subsequent /cart.js fetch failing after the Location API call succeeded).
  function parseLocationApiResponse(r) {
    if (r.ok) return r.json();
    return r.json().catch(function () { return null; }).then(function (body) {
      var message = body && body.message;
      if (!message) {
        // 502/503/504 are infrastructure-level failures (upstream unreachable, overloaded,
        // gateway timeout) — they arrive with no JSON body from Vinculum itself, so there's
        // no body.message to fall back to. A raw "Location API HTTP 502" reads as a
        // developer string, not something to show a customer in the error toast.
        message = (r.status === 502 || r.status === 503 || r.status === 504)
          ? "We're having trouble reaching the delivery service. Please try again shortly."
          : ('Location API HTTP ' + r.status);
      }
      var err = new Error(message);
      err.isLocationApiError = true;
      throw err;
    });
  }

  // ── Shared live-availability cache ────────────────────────────────────────
  // Every function that needs a per-SKU live freeQty number (validateCartDelivery,
  // revalidateCartOnLoad, revalidateExpressBadge, callMiddleware,
  // batchDeliveryToggle) reads/writes this ONE cache instead of each independently
  // hitting Vinculum. Whichever of them runs first for a SKU at this location primes
  // it for all the others — e.g. opening the mini-cart right after checkout, or
  // toggling the delivery method right after the cart page's load-time check, now
  // reuses that same confirmed number instead of firing another request.
  // Scoped to the current location: cleared wherever confirmedExpressSkus/
  // confirmedExpressLineVids already are (med7:location-saved, med7ClearExpressLocation)
  // since Vinculum's freeQty is only meaningful for the lat/lng it was fetched for.
  // sku → { invMap: {loccode: freeQty}, bestLoccode, bestFreeQty, bestDist }
  var liveAvailabilityCache = {};
  // sku → in-flight fetchLiveAvailability() promise, so two triggers asking about the
  // same SKU within one tick (e.g. cart-open + a qty-change settle) share one request.
  var pendingAvailabilityFetches = {};

  // SKUs whose live check FAILED at this location (API 500 / network error / a SKU
  // Vinculum has no data for). Distinct from "not yet checked": a failed SKU can never
  // be confirmed on this page load, so its message must be HIDDEN rather than left
  // showing "Checking availability..." forever. Cleared per-SKU by a later success and
  // wholesale by clearAvailabilityCache() on a location change.
  const failedAvailabilitySkus = new Set();

  function clearAvailabilityCache() {
    liveAvailabilityCache = {};
    pendingAvailabilityFetches = {};
    failedAvailabilitySkus.clear();
  }

  // Strips the LIVE_INVENTORY_ATTR marker from every badge on the page — called
  // alongside clearAvailabilityCache() on a location change so pdp-delivery-options.liquid
  // (which reads this marker directly off the DOM, not the JS-side cache) correctly goes
  // back to "not yet confirmed for this location" instead of treating the OLD location's
  // now-stale marker as still valid.
  function clearLiveInventoryMarkers() {
    document.querySelectorAll('[' + LIVE_INVENTORY_ATTR + ']').forEach(function (b) {
      b.removeAttribute(LIVE_INVENTORY_ATTR);
    });
  }

  // Marks every DOM badge for a SKU as carrying LIVE (not metafield) data-inventory —
  // consulted by pdp-delivery-options.liquid so the PDP delivery-method widget can wait
  // for this marker before making its first instant/next-day decision, instead of
  // deciding from the metafield snapshot and locking that decision in for good (see the
  // PDP-vs-PLP add-to-cart bug this exists to prevent). Written even when invMap is
  // empty ("confirmed: zero coverage everywhere") — that's still a settled answer, not
  // an unconfirmed one.
  var LIVE_INVENTORY_ATTR = 'data-inventory-live';
  function markBadgeLiveInventory(sku, invMap) {
    var invJson = JSON.stringify(invMap || {});
    document.querySelectorAll('[data-sku="' + sku + '"][data-inventory]').forEach(function (b) {
      b.setAttribute('data-inventory', invJson);
      b.setAttribute(LIVE_INVENTORY_ATTR, '1');
    });
  }

  // Folds raw Vinculum entries [{sku, loccode, freeQty, distance}, ...] into the cache,
  // keyed by SKU, and mirrors each SKU's full invMap onto its DOM badge(s) — every call
  // site used to do this same loccode/freeQty reduction and data-inventory write itself.
  function primeAvailabilityCache(entries) {
    var loccodes = cachedLoccodes;
    var bySku = {};
    (entries || []).forEach(function (entry) {
      var sku  = String(entry.sku);
      var lc   = String(entry.loccode);
      var qty  = Number(entry.freeQty) || 0;
      var dist = Number(entry.distance) || Infinity;
      if (!bySku[sku]) bySku[sku] = { invMap: {}, bestLoccode: '', bestFreeQty: 0, bestDist: Infinity };
      bySku[sku].invMap[lc] = qty;
      if (loccodes.indexOf(lc) !== -1 && qty > 0) {
        var d = bySku[sku];
        if (qty > d.bestFreeQty || (qty === d.bestFreeQty && dist < d.bestDist)) {
          d.bestFreeQty = qty;
          d.bestLoccode = lc;
          d.bestDist    = dist;
        }
      }
    });
    Object.keys(bySku).forEach(function (sku) {
      liveAvailabilityCache[sku] = bySku[sku];
      confirmedExpressSkus.add(sku);
      markBadgeLiveInventory(sku, bySku[sku].invMap);
    });
  }

  // ── Live cache accessors — THE source of truth for every express decision ──────
  // A SUCCESSFUL location API response is authoritative. Everything else in this file
  // that used to compute express quantity did so from metafield-derived data:
  //   • badge `data-inventory` — starts as the Liquid-rendered metafield snapshot, is
  //     overwritten with live data by markBadgeLiveInventory(), but is RESET back to the
  //     metafield by every Shopify section re-render (add-to-cart, qty change, …)
  //   • `#med7-variant-inventory` (sections/main-cart-items.liquid) — a raw Liquid dump of
  //     `variant.metafields.custom.location_inventory`, never live-corrected at all
  // That is exactly how a product correctly showing NEXT-DAY on its PDP (decided from the
  // live response) could come back as INSTANT once added to the cart (decided from the
  // metafield). These two accessors read the in-memory cache instead, which no section
  // re-render can clobber — prefer them over any DOM/Liquid inventory read.
  //
  // getLiveExpressQty returns null (NOT 0) for a SKU with no live answer yet, so callers
  // can distinguish "confirmed zero coverage" from "not checked" and refuse to claim
  // instant on the strength of metafield data alone.
  function getLiveExpressQty(sku) {
    var live = sku ? liveAvailabilityCache[sku] : null;
    return live ? (live.bestFreeQty || 0) : null;
  }

  function getLiveBestLoccode(sku) {
    var live = sku ? liveAvailabilityCache[sku] : null;
    return live ? (live.bestLoccode || '') : '';
  }

  function isLiveConfirmedSku(sku) {
    return !!(sku && liveAvailabilityCache[sku]);
  }

  // Exposed for snippets/pdp-delivery-options.liquid, whose own script is a separate IIFE
  // and cannot reach liveAvailabilityCache directly.
  window.med7GetLiveExpressQty  = getLiveExpressQty;
  window.med7GetLiveBestLoccode = getLiveBestLoccode;
  window.med7IsLiveConfirmedSku = isLiveConfirmedSku;

  // Low-level Vinculum call shared by every per-SKU/multi-SKU live check. On success,
  // primes the cache for every SKU actually present in the response, AND for any
  // requested SKU Vinculum omitted entirely (treated as confirmed zero-everywhere —
  // otherwise that SKU's message would stay on "checking" forever with no further
  // trigger able to tell the difference between "not yet checked" and "checked, no
  // coverage").
  function fetchLiveAvailability(skus, stored) {
    dbg('fetchLiveAvailability: calling Vinculum for ' + JSON.stringify(skus));
    return fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
      body: JSON.stringify({
        skus: skus,
        channelProductId: getChannelProductId(stored),
        toLatitude: String(stored.lat),
        toLongitude: String(stored.lng),
        toPinCode: '',
        channelCode: CHANNEL_CODE
      })
    })
      .then(parseLocationApiResponse)
      .then(function (response) {
        dbg('fetchLiveAvailability response:', response);
        var entries = Array.isArray(response) ? response : ((response && response.data) ? response.data : []);
        primeAvailabilityCache(entries);
        skus.forEach(function (sku) {
          if (!liveAvailabilityCache[sku]) {
            liveAvailabilityCache[sku] = { invMap: {}, bestLoccode: '', bestFreeQty: 0 };
            markBadgeLiveInventory(sku, {});
          }
          confirmedExpressSkus.add(sku);
          failedAvailabilitySkus.delete(sku); // a success supersedes any earlier failure
        });
        return entries;
      })
      .catch((err) => {
        // Record which SKUs we could not answer for, so their cart/mini-cart message is
        // hidden instead of stuck on "Checking availability..." (see _applyCartLineMaxes).
        skus.forEach(function (sku) { failedAvailabilitySkus.add(sku); });
        // err has already been through parseLocationApiResponse once (that's what threw
        // it) — it's an Error, not a Response, so it has no .json() to parse again.
        // Calling parseLocationApiResponse(err) here replaced it with a generic
        // "r.json is not a function" TypeError that had lost isLocationApiError, silently
        // breaking the toast for every caller of getLiveAvailability(). Log and re-throw
        // the original error unchanged so validateCartDelivery/revalidateExpressBadge/
        // revalidateCartOnLoad's own isLocationApiError checks work.
        dbg('fetchLiveAvailability ERROR skus=' + skus + ' stored=' + stored + ' err=' + err);
        throw err;
      });
  }

  // Public entry point every call site should use instead of fetch(API_URL, ...)
  // directly. Resolves with { sku → cache entry } covering every requested SKU that
  // has (or ends up with) live data — cached SKUs resolve with zero network activity,
  // SKUs already being fetched by another caller await that same in-flight request,
  // and only the genuinely-uncached remainder trigger a (single, batched) fetch.
  // Resolves to {} when there's no eligible location/loccodes to check against yet.
  function getLiveAvailability(skus) {
    var stored = getStoredLocation();
    if (!stored || !stored.lat || !stored.lng || !isEligibleRegion(stored)) return Promise.resolve({});
    var loccodes = (cachedLoccodes && cachedLoccodes.length) ? cachedLoccodes : getStoredLoccodes();
    if (!loccodes.length) return Promise.resolve({});

    var uncached = [];
    var waits    = [];
    (skus || []).forEach(function (sku) {
      if (!sku) return;
      if (liveAvailabilityCache[sku]) return;
      if (pendingAvailabilityFetches[sku]) { waits.push(pendingAvailabilityFetches[sku]); return; }
      if (uncached.indexOf(sku) === -1) uncached.push(sku);
    });

    var fetchPromise = uncached.length ? fetchLiveAvailability(uncached, stored) : Promise.resolve();
    if (uncached.length) {
      uncached.forEach(function (sku) { pendingAvailabilityFetches[sku] = fetchPromise; });
      fetchPromise.catch(function () {}).then(function () {
        uncached.forEach(function (sku) { delete pendingAvailabilityFetches[sku]; });
      });
    }

    // Settle each request INDIVIDUALLY. A bare Promise.all rejects the moment any one
    // of these fails, which had two nasty consequences:
    //   (a) one SKU Vinculum has no data for discarded the confirmed results for every
    //       other SKU in the same batch (revalidateCartOnLoad batches the whole cart);
    //   (b) `waits` holds promises OWNED BY ANOTHER CALLER via pendingAvailabilityFetches,
    //       so a failing PDP badge check dragged a concurrent cart check into its own
    //       .catch — which then demoted the entire cart. Both were real, shipped bugs.
    var settled = [fetchPromise].concat(waits).map(function (p) {
      // Flag the failure separately from the value: a rejection carrying a falsy reason
      // would otherwise be indistinguishable from a success.
      return Promise.resolve(p).catch(function (e) { return { __med7AvailFailed: true, err: e }; });
    });

    return Promise.all(settled).then(function (outcomes) {
      var result = {};
      (skus || []).forEach(function (sku) { if (liveAvailabilityCache[sku]) result[sku] = liveAvailabilityCache[sku]; });

      var failed   = false;
      var firstErr = null;
      outcomes.forEach(function (o) {
        if (o && o.__med7AvailFailed && !failed) { failed = true; firstErr = o.err; }
      });
      if (!failed) return result;

      if (Object.keys(result).length) {
        // Partial success — hand back what we DID confirm. Every consumer already skips
        // SKUs with no entry (applyLiveAvailabilityToCartLines' `if (!live) return;`,
        // revalidateExpressBadge's `if (!live) return;`), so the unconfirmed SKUs simply
        // keep whatever the last successful check established. Surface the failure here
        // because the caller's own .catch will not run on a resolved promise.
        dbg('getLiveAvailability: partial result — ' + Object.keys(result).length + '/' +
            (skus || []).length + ' SKUs confirmed, error: ' + firstErr);
        if (firstErr && firstErr.isLocationApiError) showLocationApiErrorToast(firstErr.message);
        return result;
      }
      // Nothing usable at all — let the caller's failure path handle it.
      throw firstErr;
    });
  }

  // ── Shared cart-line demote logic ─────────────────────────────────────────
  // The one piece of decision-making that validateCartDelivery and revalidateCartOnLoad
  // each used to reimplement independently: given a cart
  // snapshot and a { sku → live availability } map (fetched fresh or read straight
  // from the cache above), decide each line's delivery_type/45_min_available/loccode
  // and write cart_line_item_attr in ONE call if anything actually changed.
  // Corrects in BOTH directions, because the location API is the source of truth:
  //   • DEMOTE 45_minutes → next_day when live coverage no longer covers the line qty.
  //   • PROMOTE next_day → 45_minutes when live coverage DOES cover it and the cart-wide
  //     choice is instant. This exists because phaseB now refuses to claim instant for a
  //     SKU the API hasn't confirmed (it used to do so off metafield data — the cause of
  //     "next-day on the PDP, instant in the cart"). Without a promote path, any line
  //     added before its live answer arrived — every PLP/quick-add, and any PDP add that
  //     beat the API — would be stranded on next_day permanently, since phaseB's
  //     preserveExisting guard then locks the established type in.
  // Promotion requires FULL coverage of the current quantity (>=, not > 0): this function
  // only ever writes cart_line_item_attr and never a line quantity, so promoting on
  // partial coverage would leave the line's qty above its own 45_min_available with no
  // /cart/change.js cap ever applied. Partially-covered lines stay next_day; an explicit
  // delivery-method toggle (batchDeliveryToggle) still handles those, cap included.
  // options.onlyVid, when set, restricts evaluation to a single line so its check never
  // touches sibling lines' already-settled state. No current caller sets it (add-time
  // per-SKU revalidation was removed — see CLAUDE.md); kept for a future single-line path.
  function applyLiveAvailabilityToCartLines(cart, availabilityMap, options) {
    options = options || {};
    var onlyVid = options.onlyVid ? String(options.onlyVid) : null;

    var items = cart.items || [];
    var cartAttrs = cart.attributes || {};
    var existingAttrArr = [];
    try {
      if (cartAttrs.cart_line_item_attr) existingAttrArr = JSON.parse(cartAttrs.cart_line_item_attr);
    } catch (e) { /* ignore parse errors */ }

    var deliveryDataMap = {};
    existingAttrArr.forEach(function (entry) {
      var evid = String(entry.variant_id || '');
      if (evid) deliveryDataMap[evid] = entry;
    });

    var anyChange     = false;
    var anyTypeChange = false;

    items.forEach(function (item) {
      var vid = String(item.variant_id);
      if (onlyVid && vid !== onlyVid) return;
      var sku  = item.sku;
      var live = availabilityMap[sku];
      if (!live) return; // no live data for this SKU yet — leave its current state untouched

      // Got live data for this SKU — its message no longer needs to show "checking",
      // regardless of whether any attribute below actually changes.
      confirmedExpressLineVids.add(vid);

      var currentEntry      = deliveryDataMap[vid] || {};
      var currentType       = currentEntry.delivery_type          || cartAttrs['line_dt_' + vid] || 'next_day';
      var current45MinAvail = currentEntry['45_min_available']    || '0';
      var currentLoccode    = currentEntry.quick_delivery_loccode || '';

      var liveExpressQty = live.bestFreeQty || 0;
      var bestLoccode    = live.bestLoccode || '';

      var wasInstant  = currentType === '45_minutes';
      // Demote strictly on live COVERAGE — never on the cart-wide med7_delivery_method
      // choice. Enforcing that radio belongs to batchDeliveryToggle / phaseB; a live
      // availability check has exactly one job. The old rule also OR'd in
      // `getDeliveryChoice() === 'nextday'`, which killed every legitimate mixed-state
      // add: med7CartAddPrecheck deliberately creates a 45_minutes line while the
      // cart-wide choice is still nextday, and revalidateAddedSku (running microseconds
      // later, on that same vid) would flip it straight back to next_day — after which
      // phaseB's preserveExisting guard locked it in and nothing could ever promote it.
      // That was a real, shipped bug; the choice must not re-enter this decision.
      var mustDemote  = wasInstant && liveExpressQty < item.quantity;

      // Promote when the API confirms coverage for the full line quantity AND the
      // cart-wide choice is instant. The choice guard means an explicit next-day toggle
      // is never fought — batchDeliveryToggle/applyNoEligibleStores write that choice
      // synchronously before any live check can run, so this always reads the current
      // value. This is the ONLY path by which a line held at next_day by phaseB's
      // not-live-confirmed guard can reach 45_minutes.
      // options.intendedChoice: lets a caller override the promotion check with an intent
      // other than the cart-wide choice. No current caller sets it (the add-time caller
      // that used to pass the PDP/card add intent was removed — see CLAUDE.md); falls
      // back to the cart-wide choice for both current callers (revalidateCartOnLoad,
      // validateCartDelivery), which have no per-line intent to speak of.
      var promoteIntent = options.intendedChoice || getDeliveryChoice();
      var mustPromote = !wasInstant &&
        promoteIntent === 'instant' &&
        liveExpressQty >= item.quantity;

      var desiredType = mustDemote ? 'next_day' : (mustPromote ? '45_minutes' : currentType);

      var desiredAvail, desiredLoccode;
      if (mustDemote) {
        // Zero out so a future switch-to-instant can't treat stale availability as
        // still eligible for re-promotion.
        desiredAvail   = '0';
        desiredLoccode = '';
      } else {
        desiredAvail   = String(liveExpressQty);
        desiredLoccode = liveExpressQty > 0 ? bestLoccode : '';
      }

      if (desiredType    === currentType       &&
          desiredAvail   === current45MinAvail &&
          desiredLoccode === currentLoccode) {
        deliveryDataMap[vid] = Object.assign({ variant_id: vid }, currentEntry);
        return;
      }

      if (desiredType !== currentType) anyTypeChange = true;
      anyChange = true;
      deliveryDataMap[vid] = Object.assign({}, currentEntry, {
        variant_id:             vid,
        delivery_type:          desiredType,
        quick_delivery_loccode: desiredLoccode,
        '45_min_available':     desiredAvail,
        quick_delivery_qty:     desiredType === '45_minutes' ? String(item.quantity) : '0',
        standard_delivery_qty:  desiredType === 'next_day'   ? String(item.quantity) : '0'
      });
    });

    if (!anyChange) return Promise.resolve({ changed: false, typeChanged: false, cart: cart });

    return saveCartLineItemAttr(cart, deliveryDataMap, {}, options.callerLabel || 'applyLiveAvailabilityToCartLines')
      .then(function (updatedCart) {
        return { changed: true, typeChanged: anyTypeChange, cart: updatedCart || cart };
      });
  }

  // ── Location API error toast ──────────────────────────────────────────────
  // DEV-ONLY as of 2026-08-05: gated behind DEBUG because the backend's error
  // responses are not currently reliable enough to surface to customers — a
  // toast that fires on legitimate-looking traffic erodes trust worse than
  // silently holding state (see handleLiveCheckFailure, which never demotes
  // on failure regardless of whether the toast is showing). Flip DEBUG=true to
  // see it while testing. When the backend stabilizes, remove the early return
  // below to restore it as the customer-facing surface it was originally built
  // to be — do not delete the toast implementation itself.
  // CSS is injected once into <head> and the toast element is created once and
  // reused (mirroring showDeliveryLoader()'s pattern below) so near-simultaneous
  // failures from different callers refresh one toast instead of stacking duplicates.
  // Anchored to the top of the viewport and persistent — no auto-hide timer — so it
  // can't be missed and can't disappear before it's been read; dismissed only via
  // its own close button.
  function hideLocationApiErrorToast() {
    var toast = document.getElementById('med7-location-error-toast');
    if (toast) toast.classList.remove('is-visible');
  }

  function showLocationApiErrorToast(message) {
    if (!DEBUG) return;
    if (!document.getElementById('med7-location-error-toast-style')) {
      var styleEl = document.createElement('style');
      styleEl.id = 'med7-location-error-toast-style';
      styleEl.textContent =
        '.med7-location-error-toast{' +
          'position:fixed;left:16px;right:16px;top:16px;margin:0 auto;max-width:380px;' +
          'background:#fff;border-left:4px solid #e53e3e;border-radius:8px;' +
          'box-shadow:0 4px 20px rgba(0,0,0,0.15);padding:14px 16px;' +
          'z-index:99999;display:-webkit-box;display:-ms-flexbox;display:flex;' +
          '-webkit-box-align:start;-ms-flex-align:start;align-items:flex-start;gap:10px;' +
          'opacity:0;-webkit-transform:translateY(-12px);transform:translateY(-12px);' +
          '-webkit-transition:opacity .25s ease,-webkit-transform .25s ease;' +
          'transition:opacity .25s ease,transform .25s ease;' +
          'pointer-events:none;' +
        '}' +
        '.med7-location-error-toast.is-visible{opacity:1;-webkit-transform:translateY(0);transform:translateY(0);pointer-events:auto;}' +
        '.med7-location-error-toast__body{-webkit-box-flex:1;-ms-flex:1;flex:1;min-width:0;}' +
        '.med7-location-error-toast__heading{font-weight:600;font-size:14px;color:#1a1a1a;margin:0 0 2px;}' +
        '.med7-location-error-toast__message{font-size:13px;color:#5a5a5a;margin:0;word-break:break-word;}' +
        '.med7-location-error-toast__close{-webkit-box-flex:0;-ms-flex:0 0 auto;flex:0 0 auto;background:none;border:none;padding:2px;cursor:pointer;color:#8a8a8a;font-size:16px;line-height:1;}' +
        '@media (min-width:576px){.med7-location-error-toast{left:auto;right:16px;margin:0;}}' +
        'html[dir="rtl"] .med7-location-error-toast{border-left:none;border-right:4px solid #e53e3e;}' +
        '@media (min-width:576px){html[dir="rtl"] .med7-location-error-toast{right:auto;left:16px;}}';
      document.head.appendChild(styleEl);
    }

    var toast = document.getElementById('med7-location-error-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'med7-location-error-toast';
      toast.className = 'med7-location-error-toast';
      toast.setAttribute('role', 'alert');
      toast.innerHTML =
        '<div class="med7-location-error-toast__body">' +
          '<p class="med7-location-error-toast__heading"></p>' +
          '<p class="med7-location-error-toast__message"></p>' +
        '</div>' +
        '<button type="button" class="med7-location-error-toast__close" aria-label="Dismiss">&times;</button>';
      toast.querySelector('.med7-location-error-toast__close').addEventListener('click', hideLocationApiErrorToast);
      document.body.appendChild(toast);
    }

    var heading = (window.med7ExpressDeliveryStrings && window.med7ExpressDeliveryStrings.locationApiErrorHeading) ||
      "We couldn't check delivery availability";
    toast.querySelector('.med7-location-error-toast__heading').textContent = heading;
    toast.querySelector('.med7-location-error-toast__message').textContent = message || '';

    // Remove/re-add is-visible (with a forced reflow) so the transition replays
    // even if the toast is already showing from a previous, still-visible error.
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
  }

  function getDeliveryChoice() {
    try { return localStorage.getItem(DELIVERY_CHOICE_KEY) || 'instant'; } catch(e) { return 'instant'; }
  }

  function saveDeliveryChoice(choice) {
    try { localStorage.setItem(DELIVERY_CHOICE_KEY, choice); } catch(e) {}
    // Spec §3B: also persist to cart attribute (fire-and-forget)
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { med7_delivery_method: choice } })
    }).catch(function() {});
  }

  function isDeliveryChoiceExplicit() {
    try { return localStorage.getItem(DELIVERY_CHOICE_EXPLICIT_KEY) === '1'; } catch(e) { return false; }
  }

  function markDeliveryChoiceExplicit() {
    try { localStorage.setItem(DELIVERY_CHOICE_EXPLICIT_KEY, '1'); } catch(e) {}
  }

  function clearDeliveryChoiceExplicit() {
    try { localStorage.removeItem(DELIVERY_CHOICE_EXPLICIT_KEY); } catch(e) {}
  }

  // Reads the PDP-specific add choice (set when user changes the PDP radio).
  // Falls back to the cart-wide choice so precheck works correctly even when
  // the PDP widget hasn't initialised yet (e.g. product-card quick-add).
  function getPDPAddChoice() {
    try { return localStorage.getItem('med7_pdp_add_choice') || getDeliveryChoice(); } catch(e) { return getDeliveryChoice(); }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function getStoredLocation() {
    try {
      const raw = localStorage.getItem(LOCATION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt || (Date.now() - parsed.savedAt) > MAX_LOCATION_AGE_MS) return null;
      return parsed;
    } catch (e) { return null; }
  }

  // ── Region gate: city/emirate-based eligibility matching has been turned off —
  // every saved location is treated as eligible.
  function isEligibleRegion(location) {
    return !!location;
  }

  // ── Location API channel routing: channelCode is now hardcoded to "M06" for
  // every request regardless of emirate (previously M08 for Ras Al Khaimah).
  // The per-region distinction is now carried by channelProductId instead —
  // see getChannelProductId below.
  const CHANNEL_CODE = 'M06';

  // channelProductId is always Mapbox's stable region identifier (e.g. "AE-RK",
  // "AE-DU"), sent as a single-element array. Used on every Vinculum call —
  // including on the home page where there is no real SKU yet, in which case
  // `sku` also carries this same region code as a placeholder (see callMiddleware).
  function getChannelProductId(location) {
    return [(location && location.region_code) || ''];
  }

  // Timestamp lives in a sibling key (not wrapped into LOCCODE_KEY itself) — several other
  // scripts/snippets (e.g. pdp-delivery-options.liquid) read LOCCODE_KEY directly and expect
  // a bare array, so its stored shape must stay unchanged.
  const LOCCODE_SAVED_AT_KEY = 'med7_loccode_saved_at';

  function getStoredLoccodes() {
    try {
      const savedAt = Number(localStorage.getItem(LOCCODE_SAVED_AT_KEY));
      if (!savedAt || (Date.now() - savedAt) > MAX_LOCATION_AGE_MS) return [];
      const raw = localStorage.getItem(LOCCODE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : (parsed ? [String(parsed)] : []);
    } catch (e) { return []; }
  }

  function saveLoccodes(loccodes) {
    try {
      localStorage.setItem(LOCCODE_KEY, JSON.stringify(loccodes));
      localStorage.setItem(LOCCODE_SAVED_AT_KEY, String(Date.now()));
    } catch (e) {}
  }

  function clearLoccodes() {
    try {
      localStorage.removeItem(LOCCODE_KEY);
      localStorage.removeItem(LOCCODE_SAVED_AT_KEY);
    } catch (e) {}
  }


  // ── Delivery choice after a location change (Spec §4/§5, revised 2026-08-05) ──
  // Decides the cart-wide med7_delivery_method for a NEW location from ONE rule:
  //
  //     instant  ⟺  the customer was ALREADY on instant
  //                 AND at least one currently-instant line is still coverable here
  //
  // Every case the spec calls for falls out of that single predicate:
  //   • next-day location → instant location: prevChoice is 'nextday', so it STAYS
  //     nextday. An explicit next-day choice is never silently upgraded just because the
  //     new location happens to have coverage — both radios render and the customer opts
  //     in themselves. (The old implementation ignored prevChoice entirely and promoted
  //     to instant whenever any cart SKU had coverage. That was the bug.)
  //   • instant → instant: the LIVE response decides, not the metafield. If nothing that
  //     was instant can stay instant here, there is nothing to retain → nextday.
  //   • instant → next-day location: never reaches this function — callMiddleware's
  //     zero-loccode branch calls applyNoEligibleStores(), which forces nextday.
  //
  // Two deliberate choices worth knowing:
  //
  // 1. prevChoice comes from cart.attributes.med7_delivery_method (the server-side source
  //    of truth), NOT localStorage. The med7:location-saved handler clears caches and
  //    mutates the DOM before we get here — which can wake observeCartContainers and run a
  //    whole processCartDelivery pass — and several paths can write localStorage. The cart
  //    attribute on the snapshot we already hold is the only value guaranteed untouched by
  //    this location change. Falls back to getDeliveryChoice() only if the attribute is
  //    absent (a cart that predates the attribute).
  //
  // 2. Coverage test is `bestFreeQty > 0`, not `>= item.quantity`, so it predicts exactly
  //    what phaseB does next: on a location change phaseB runs with preserveExisting=false
  //    and makes any line with expressQty > 0 instant, capping qty to expressQty. Using
  //    `>= quantity` here would set nextday in cases where phaseB would have happily kept
  //    lines instant. Trade-off: a line whose coverage shrank gets its quantity capped
  //    (units silently dropped) rather than demoted to next-day.
  //
  // Returns a promise and NEVER rejects. callMiddleware must await it before calling
  // scheduleCartDelivery, because phaseB derives every line's delivery_type from
  // getDeliveryChoice().
  function recalculateDeliveryChoice(cart) {
    var items = (cart && cart.items) || [];
    var attrs = (cart && cart.attributes) || {};
    var attrChoice = attrs.med7_delivery_method;
    var prevChoice = (attrChoice === 'instant' || attrChoice === 'nextday')
      ? attrChoice
      : getDeliveryChoice();

    // Compares the target against the actual cart attribute (attrChoice), not localStorage.
    // med7CartAddPrecheck and phaseB/processCartDelivery never write med7_delivery_method
    // themselves (only line-level attributes) — this commit() is the only place a location
    // change puts the recalculated choice onto the cart attribute. Comparing against
    // localStorage instead let the /cart/update.js write get skipped whenever localStorage
    // already happened to agree, even when the attribute itself was missing or stale (e.g.
    // a fresh/cleared cart that never had it set) — so an empty cart's attribute could stay
    // wrong indefinitely after a location change. attrChoice is undefined whenever the
    // attribute hasn't been set at all, which always writes (can't confirm it's already
    // correct), matching "update the cart attribute even if the cart is empty."
    var commit = function (newChoice, why) {
      dbg('recalculateDeliveryChoice: ' + prevChoice + ' → ' + newChoice + ' (' + why + ')');
      if (newChoice !== attrChoice) {
        saveDeliveryChoice(newChoice);
      } else if (newChoice !== getDeliveryChoice()) {
        // Attribute already correct but localStorage had drifted — just re-sync it locally,
        // no need for another network write.
        try { localStorage.setItem(DELIVERY_CHOICE_KEY, newChoice); } catch (e) {}
      }
      return newChoice;
    };

    // Empty cart: no products exist that could invalidate — or justify — any particular
    // choice. Checked BEFORE the "was on next-day → never auto-promote" rule below,
    // because that rule only makes sense for a REAL preference — and prevChoice being
    // 'nextday' is not always one: applyNoEligibleStores() force-sets it whenever a
    // location has no coverage at all, which is a system default, not something the
    // customer chose. isDeliveryChoiceExplicit() is the one thing that tells the two
    // apart (see markDeliveryChoiceExplicit/clearDeliveryChoiceExplicit). Already being on
    // instant, or an explicit next-day pick, is left untouched either way — there's
    // nothing to re-evaluate on an empty cart regardless; the next add-to-cart's own
    // precheck is what really decides. Non-empty carts still fall through to the
    // unconditional "no auto-promote" rule below — this reset is deliberately scoped to
    // the empty-cart case, where nothing has ever actually depended on the forced value.
    if (!items.length) {
      if (prevChoice === 'instant' || isDeliveryChoiceExplicit()) {
        return Promise.resolve(commit(prevChoice, 'empty cart — nothing to re-evaluate'));
      }
      return Promise.resolve(commit('instant',
        'empty cart and prior next-day was a forced default (no coverage elsewhere), not an explicit choice'));
    }

    // Was on next-day → never auto-promote. No API call needed at all.
    if (prevChoice !== 'instant') {
      return Promise.resolve(commit('nextday', 'was not on instant — no auto-promote'));
    }

    // Only lines that are CURRENTLY instant can "retain" instant.
    var instantItems = items.filter(function (item) {
      return attrs['line_dt_' + item.variant_id] === '45_minutes';
    });
    if (!instantItems.length) {
      return Promise.resolve(commit('nextday', 'no currently-instant lines to retain'));
    }

    var skus = [];
    instantItems.forEach(function (item) {
      if (item.sku && skus.indexOf(item.sku) === -1) skus.push(item.sku);
    });
    if (!skus.length) {
      return Promise.resolve(commit('nextday', 'currently-instant lines carry no SKU'));
    }

    // ONE batched live call covering every currently-instant SKU. This also primes the
    // shared liveAvailabilityCache, so the validateCartDelivery / revalidateCartOnLoad
    // passes that follow this same location change resolve from cache instead of hitting
    // Vinculum again — net zero extra API calls across the location change.
    return getLiveAvailability(skus)
      .then(function (availabilityMap) {
        var canRetain = instantItems.some(function (item) {
          var live = availabilityMap[item.sku];
          return !!live && (live.bestFreeQty || 0) > 0;
        });
        return commit(canRetain ? 'instant' : 'nextday',
          canRetain ? 'an instant line is still covered here'
                    : 'no instant line is covered at the new location');
      })
      .catch(function (err) {
        // Could not verify. Hold the existing choice rather than demoting off an
        // unverified failure — same philosophy as handleLiveCheckFailure. If coverage
        // really is gone, the next successful live check demotes the lines.
        dbg('recalculateDeliveryChoice: live check failed, holding ' + prevChoice, err);
        return commit(prevChoice, 'live check failed — holding previous choice');
      });
  }

  // ── Spec §9A: update cart delivery widget after attribute save ───────────
  // Dispatches a custom event so every initialised cart/mini-cart widget can
  // re-apply availability without a full DOM re-render. Includes both items and
  // attributes so cart-delivery-options.js can update parsedState without an
  // extra /cart.js fetch.
  // attributes is dispatched as null — never {} — when parsedState holds no cart
  // attributes. cart-delivery-options.js only overwrites its own cached copy when this
  // field is truthy, so an empty object would cement a blank attribute map over a good
  // one (and {} is truthy). parsedState legitimately lacks attributes whenever a
  // sections-render blob or a /cart/add.js response was the last thing written to it —
  // see the note on syncParsedStateCache.
  function updateCartDeliveryWidget() {
    try {
      var ps    = JSON.parse(localStorage.getItem('parsedState') || 'null');
      var items = (ps && Array.isArray(ps.items)) ? ps.items : [];
      var attrs = (ps && ps.attributes && typeof ps.attributes === 'object') ? ps.attributes : null;
      window.dispatchEvent(new CustomEvent('med7:cart-delivery-updated', { detail: { items: items, attributes: attrs } }));
    } catch (e) {
      window.dispatchEvent(new CustomEvent('med7:cart-delivery-updated', { detail: { items: [], attributes: null } }));
    }
  }

  // ── Badge display — reads data-inventory from each badge element ───────────

  function updateAllBadges(loccodes) {
    const badges = document.querySelectorAll('.badge-express-delivery');
    // PDP/Quick View badges get a real live check; PLP/product-card badges never do
    // (by design — see the "PLP/product-card badges keep reading the metafield" rule)
    // and must keep falling through to the metafield estimate below.
    const pdpBadge = getPDPBadge();
    const quickViewBadge = getQuickViewBadge();

    badges.forEach(function (badge) {
      // Cart-item badges have Liquid-rendered visibility — skip JS control
      if (badge.getAttribute('data-cart-item')) return;

      const container = badge.parentElement;
      const nextDayBadge = container ? container.querySelector('.badge-next-day-delivery') : null;

      if (!loccodes || !loccodes.length) {
        badge.style.display = 'none';
        if (nextDayBadge) nextDayBadge.style.display = '';
        return;
      }

      const invAttr = badge.getAttribute('data-inventory');
      const sku = badge.getAttribute('data-sku');

      // Live answer wins for any SKU the API has already confirmed — PDP, Quick View and
      // cart badges all land here. PLP/product-card SKUs typically have no live entry, so
      // they fall through to the metafield-driven path below, which is the intended
      // behaviour for PLP. Reading the cache rather than data-inventory also survives the
      // section re-renders that reset that attribute back to the metafield snapshot.
      const liveBadgeQty = getLiveExpressQty(sku);
      if (liveBadgeQty !== null) {
        const showExpressLive = liveBadgeQty > 0;
        badge.style.display = showExpressLive ? '' : 'none';
        if (nextDayBadge) nextDayBadge.style.display = showExpressLive ? 'none' : '';
        return;
      }

      // This badge WILL get a live answer (it's the PDP's or the open Quick View's own
      // badge) but hasn't yet, and a location with potential coverage is already known
      // (loccodes.length, checked above). Hold both badges hidden instead of guessing from
      // the metafield snapshot — that guess is what let a next-day product flash an
      // "instant" badge for a beat before the live check corrected it.
      if (badge === pdpBadge || badge === quickViewBadge) {
        badge.style.display = 'none';
        if (nextDayBadge) nextDayBadge.style.display = 'none';
        return;
      }

      if (!invAttr) {
        badge.style.display = 'none';
        if (nextDayBadge) nextDayBadge.style.display = '';
        return;
      }

      try {
        const inv = JSON.parse(invAttr);
        const skuInv = (sku && inv[sku]) ? inv[sku] : inv;

        // Spec §15/§0: highest single-location inventory — NOT a sum
        let expressQty = 0;
        loccodes.forEach(function (loccode) {
          const q = skuInv[loccode] !== undefined ? Number(skuInv[loccode]) : 0;
          if (q > expressQty) expressQty = q;
        });

        const showExpress = expressQty > 0;
        badge.style.display = showExpress ? '' : 'none';
        if (nextDayBadge) nextDayBadge.style.display = showExpress ? 'none' : '';
      } catch (e) {
        console.error('[ExpressDelivery] Failed to parse data-inventory:', invAttr, e);
        badge.style.display = 'none';
        if (nextDayBadge) nextDayBadge.style.display = '';
      }
    });

    // Also show next-day badge on containers that have no express badge at all
    document.querySelectorAll('.badge-next-day-delivery').forEach(function(ndb) {
      // Skip if already handled above (sibling of an express badge)
      const container = ndb.parentElement;
      if (container && container.querySelector('.badge-express-delivery:not([data-cart-item])')) return;
      // No express badge in this container — always show next day
      ndb.style.display = '';
    });

    // Update PDP/Quick-View delivery message(s) (below ATC button — identified by absence
    // of data-line; cart/mini-cart line messages have data-line and are handled by
    // _applyCartLineMaxes instead). querySelectorAll (not querySelector) because a PDP
    // message and a Quick View message for a different product can be on screen at once
    // (e.g. Quick View opened from a related-products carousel on a PDP). Each is scoped
    // to its own containing section/prd-block so unrelated messages/badges never bleed
    // into each other. Text is never derived from a not-yet-API-confirmed data-inventory
    // value (that may still be the metafield snapshot) — it shows a "checking" placeholder
    // until revalidateExpressBadge() confirms that badge's SKU. This does not affect the
    // quantity max logic in pdp-delivery-options.liquid, which reads data-inventory directly.
    document.querySelectorAll('.express-delivery-msg:not([data-line])').forEach(function (msgEl) {
      if (!loccodes || !loccodes.length) {
        msgEl.style.display = 'none';
        return;
      }

      const msgSection = msgEl.closest('.shopify-section, [data-section-id], .prd-block') || document.body;
      const msgBadge = msgSection.querySelector('.badge-express-delivery[data-inventory]');

      // No express badge in this section at all (e.g. a sold-out variant, whose
      // location_inventory metafield is blank so badge-express-delivery.liquid never
      // renders one) — there is no SKU to ever confirm and no live check will ever run
      // for it (getPDPBadge()/revalidatePDPOnLoad() need a badge to find). Showing the
      // "checking" placeholder here would leave it stuck forever; hide instead.
      if (!msgBadge) {
        msgEl.style.display = 'none';
        return;
      }

      const msgSku = msgBadge.getAttribute('data-sku');

      // Live check already FAILED for this SKU at this location — it can never become
      // confirmed on this page load, so the placeholder would spin forever. Hide instead.
      // Checked before the unconfirmed branch below, which cannot tell "still waiting"
      // from "already failed". Needed because getLiveAvailability now resolves partially:
      // when a batch owned by another caller (e.g. revalidateCartOnLoad over the whole
      // cart) half-succeeds, revalidateExpressBadge's .then sees no entry for this SKU and
      // returns early WITHOUT reaching its own .catch → hideExpressMsgForSku never runs.
      if (failedAvailabilitySkus.has(msgSku)) {
        msgEl.style.display = 'none';
        return;
      }

      if (!confirmedExpressSkus.has(msgSku)) {
        if (msgEl.textContent !== getPDPCheckingText()) msgEl.textContent = getPDPCheckingText();
        msgEl.style.display = '';
        return;
      }

      // Reaching here means confirmedExpressSkus.has(msgSku), so a live figure exists —
      // use it directly rather than re-deriving from data-inventory, which a section
      // re-render may have reset back to the metafield snapshot since. The metafield
      // parse stays only as a defensive fallback.
      let msgQty = 0;
      const liveMsgQty = getLiveExpressQty(msgSku);
      if (liveMsgQty !== null) {
        msgQty = liveMsgQty;
      } else {
        try {
          const inv = JSON.parse(msgBadge.getAttribute('data-inventory'));
          const skuInv = (msgSku && inv[msgSku]) ? inv[msgSku] : inv;
          loccodes.forEach(function (loccode) {
            const q = skuInv[loccode] !== undefined ? Number(skuInv[loccode]) : 0;
            if (q > msgQty) msgQty = q;
          });
        } catch (e) {}
      }

      // Security check: never show the message unless the express badge sitting right
      // next to it is actually visible right now. msgQty is computed from msgBadge's own
      // data-inventory, which should already agree with whatever set msgBadge's display —
      // but if this section contains more than one product's badge (e.g. a related-products
      // carousel sharing the same .shopify-section as the main PDP form), msgSection's
      // querySelector above can resolve to a different badge than the one actually driving
      // this message's context. Checking the found badge's own rendered visibility doesn't
      // depend on that match being exact, and guarantees the message is never shown next to
      // what the customer sees as a next-day badge.
      if (msgQty > 0 && msgBadge.style.display !== 'none') {
        const msgText = buildQtyMaxTooltip(msgQty);
        if (msgEl.textContent !== msgText) msgEl.textContent = msgText;
        msgEl.style.display = '';
      } else {
        msgEl.style.display = 'none';
      }
    });

    updateProductCardQtyMaxes();
    // Signal PDP delivery widget to re-apply choice-based badge swap
    document.dispatchEvent(new CustomEvent('med7:badges-updated'));
  }

  // ── Inventory map builder ─────────────────────────────────────────────────
  // Collects { sku → inventoryJson } from all badge data-inventory attributes in DOM.

  function buildInventoryMap() {
    const map = {};
    // Check both express and next-day badges — cart renders whichever is active for each item
    const badges = document.querySelectorAll(
      '.badge-express-delivery[data-sku][data-inventory],' +
      '.badge-next-day-delivery[data-sku][data-inventory]'
    );
    badges.forEach(function (badge) {
      const sku = badge.getAttribute('data-sku');
      const invAttr = badge.getAttribute('data-inventory');
      if (sku && invAttr && !map[sku]) {
        try { map[sku] = JSON.parse(invAttr); } catch (e) { console.error('[ExpressDelivery] buildInventoryMap — invalid JSON for SKU:', sku, e); }
      }
    });
    // Fallback: Liquid JSON block covers SKUs whose badges have blank data-inventory
    // (variant has no metafield) or whose badge isn't rendered at all
    var jsonBlock = document.getElementById('med7-variant-inventory') ||
                   document.getElementById('med7-variant-inventory-mini');
    if (jsonBlock) {
      try {
        var blockData = JSON.parse(jsonBlock.textContent);
        if (blockData && typeof blockData === 'object') {
          Object.keys(blockData).forEach(function (sku) {
            if (!map[sku] && blockData[sku] && typeof blockData[sku] === 'object') {
              map[sku] = blockData[sku];
            }
          });
        }
      } catch (e) { console.error('[ExpressDelivery] buildInventoryMap — JSON block parse error:', e); }
    }
    // ── Live data OVERRIDES both metafield sources above ──────────────────────────
    // Must be last so it wins. Both sources above are metafield-derived and can be
    // wrong for the current location: `data-inventory` is reset to the metafield by
    // every section re-render (discarding markBadgeLiveInventory's correction), and
    // #med7-variant-inventory is a raw Liquid metafield dump that is never corrected.
    // The in-memory cache is the only representation a re-render cannot clobber.
    Object.keys(liveAvailabilityCache).forEach(function (sku) {
      map[sku] = liveAvailabilityCache[sku].invMap || {};
    });
    dbg('buildInventoryMap keys:', Object.keys(map));
    return map;
  }

  // ── Cart delivery type logic ──────────────────────────────────────────────
  // For each cart line item, sums inventory across all cached loccodes from
  // the variant metafield, then updates delivery type properties and cart messages.

  let _cartDeliveryInFlight = false;
  let _cartDeliveryTimer = null;
  let _pendingCartDelivery = null; // saved call that arrived while inFlight
  let _qtyChangePending = false;
  let _validateCartTimer = null;
  let _deduplicating = false;
  let _cartMaxTimer = null;
  var _cartMaxLoading = false; // true on /cart page until revalidateCartOnLoad completes

  // Sequential cart line updates — Shopify does not handle concurrent writes to
  // the same cart reliably; parallel requests can overwrite each other.
  function applyCartUpdates(updates, onComplete) {
    return updates.reduce(function (chain, update) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(update)
        }).then(function (r) { return r.json(); });
      });
    }, Promise.resolve())
      .then(function () {
        // Do not call refreshCartDisplay here — all written properties are hidden
        // from Liquid templates via med7_hidden_props; a re-render would only cause
        // cart flicker. deduplicateCart handles its own refresh when quantities change.
        if (onComplete) onComplete();
      })
      .catch(function (err) {
        console.error('[ExpressDelivery] cart update failed:', err);
        if (onComplete) onComplete();
      });
  }

  // ── Save delivery metadata to cart attribute (replaces N line property writes) ──
  // Builds the cart_line_item_attr JSON array from cart items + a per-variant
  // delivery data map, then POSTs to /cart/update.js in a SINGLE call.
  // deliveryDataMap: { "variant_id_string": { delivery_type, quick_delivery_loccode, "45_min_available" } }
  // extraAttrs: additional cart attributes to merge into the same update (optional).
  // Returns the /cart/update.js response promise (full updated cart JSON).
  function saveCartLineItemAttr(cart, deliveryDataMap, extraAttrs, callerLabel) {
    var items        = cart.items       || [];
    var currentAttrs = cart.attributes  || {};
    // Flat attributes — one key per field per variant, no JSON parsing needed in Liquid.
    // Key schema: line_dt_{vid}, line_lc_{vid}, line_am_{vid}
    var attrs = Object.assign({}, extraAttrs || {});

    // ── Stale-key cleanup ──────────────────────────────────────────────────────
    // Any line_* key whose variant is no longer in cart gets blanked.
    // Shopify removes cart attributes whose value is set to an empty string.
    var activeVids = {};
    items.forEach(function (item) { activeVids[String(item.variant_id)] = true; });
    Object.keys(currentAttrs).forEach(function (key) {
      var m = key.match(/^line_(?:dt|lc|am)_(\d+)$/);
      if (m && !activeVids[m[1]]) {
        attrs[key] = '';   // blank → Shopify removes the attribute
      }
    });

    // ── Write current items ────────────────────────────────────────────────────
    var attrArray = [];
    items.forEach(function (item) {
      var vid  = String(item.variant_id);
      var data = deliveryDataMap[vid] || {};
      var entry = {
        variant_id:             vid,
        title:                  item.title,
        delivery_type:          data.delivery_type          || 'next_day',
        quick_delivery_loccode: data.quick_delivery_loccode || '',
        '45_min_available':     data['45_min_available']    || '0'
      };
      attrArray.push(entry);
      attrs['line_dt_' + vid] = entry.delivery_type;
      attrs['line_lc_' + vid] = entry.quick_delivery_loccode;
      attrs['line_am_' + vid] = entry['45_min_available'];
    });
    // Human-readable JSON blob for order processing / admin readability.
    // Liquid rendering uses the flat line_* keys above (no parse_json needed).
    attrs.cart_line_item_attr = JSON.stringify(attrArray);

    if (DEBUG) {
      console.group('[MED7:saveCartLineItemAttr] POST /cart/update.js — caller: ' + (callerLabel || 'unknown'));
      console.log('payload:', JSON.parse(JSON.stringify(attrs)));
      console.groupEnd();
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { return r.json(); });
  }

  function scheduleCartDelivery(loccodes, preserveExisting, prefetchedCart) {
    dbg('scheduleCartDelivery loccodes=' + JSON.stringify(loccodes) + ' preserveExisting=' + !!preserveExisting);
    clearTimeout(_cartDeliveryTimer);
    _cartDeliveryTimer = setTimeout(function () {
      // Pass through the pre-fetched cart so processCartDelivery can skip its own /cart.js fetch.
      processCartDelivery(loccodes, preserveExisting, prefetchedCart || null);
    }, 150); // reduced from 500 ms — dedup debounce (100 ms) already covers burst changes
  }

  // ── Cart deduplication ────────────────────────────────────────────────────
  // Shopify only merges cart line items when variant_id AND all properties are
  // identical. Once processCartDelivery writes our hidden properties to a line,
  // a subsequent add-to-cart for the same variant creates a new line (no props)
  // that cannot merge with the existing one (has props). This function detects
  // variant_id collisions, sums their quantities into the first occurrence, and
  // removes duplicates — always in reverse line-number order so that earlier
  // line numbers remain valid after each sequential removal.
  // onComplete is called after merging (or immediately when no duplicates exist).

  // onComplete(latestCart) — always receives the freshest cart so callers can
  // pass it straight into processCartDelivery without an extra /cart.js fetch.
  function deduplicateCart(onComplete) {
    if (_deduplicating) return;
    _deduplicating = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const items = cart.items || [];

        const seen = {};          // variant_id → { firstIndex, totalQty }
        const duplicateLines = []; // 1-based line numbers to remove, reverse order
        let hasDuplicates = false;

        items.forEach(function (item, i) {
          const vid = String(item.variant_id);
          if (seen[vid] === undefined) {
            seen[vid] = { firstIndex: i, totalQty: item.quantity };
          } else {
            seen[vid].totalQty += item.quantity;
            duplicateLines.unshift(i + 1);
            hasDuplicates = true;
          }
        });

        if (!hasDuplicates) {
          _deduplicating = false;
          if (onComplete) onComplete(cart); // pass through — no /cart.js re-fetch needed downstream
          return;
        }

        // Removals first (descending line order, already correct via unshift), then the
        // qty update on the surviving line. Removals must come first so that when we set
        // the merged quantity Shopify only sees one line for that variant — otherwise the
        // combined total of both lines would exceed inventory and Shopify would reject or
        // cap the update, leaving the surviving line at the wrong quantity.
        const updates = [];
        duplicateLines.forEach(function (line) {
          updates.push({ line: line, quantity: 0 });
        });
        Object.keys(seen).forEach(function (vid) {
          const group = seen[vid];
          if (items[group.firstIndex].quantity !== group.totalQty) {
            updates.push({ line: group.firstIndex + 1, quantity: group.totalQty });
          }
        });

        updates.reduce(function (chain, update) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(update)
            }).then(function (r) { return r.json(); });
          });
        }, Promise.resolve())
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(function (updatedCart) {
            refreshCartDisplay(updatedCart);
            _deduplicating = false;
            if (onComplete) onComplete(updatedCart); // pass fresh cart forward
          })
          .catch(function (err) {
            console.error('[ExpressDelivery] deduplicateCart failed:', err);
            _deduplicating = false;
            if (onComplete) onComplete(null);
          });
      })
      .catch(function (err) {
        console.error('[ExpressDelivery] deduplicateCart fetch failed:', err);
        _deduplicating = false;
        if (onComplete) onComplete(null);
      });
  }

  function processCartDelivery(loccodes, preserveExisting, prefetchedCart) {
    dbg('processCartDelivery called loccodes=' + JSON.stringify(loccodes) + ' preserveExisting=' + !!preserveExisting + ' inFlight=' + _cartDeliveryInFlight + ' skipFlag=' + window._med7SkipNextProcessCart + ' choice=' + getDeliveryChoice());
    if (!loccodes) { dbg('processCartDelivery BAILED (no loccodes)'); return; }
    if (_cartDeliveryInFlight) {
      // Save the call; replay after the in-flight run completes.
      // A preserveExisting=false call always wins over true (location change > routine update).
      if (!_pendingCartDelivery || !preserveExisting) {
        _pendingCartDelivery = { loccodes: loccodes, preserveExisting: preserveExisting };
        dbg('processCartDelivery QUEUED (inFlight) preserveExisting=' + !!preserveExisting);
      }
      return;
    }

    // PDP form submit sets this flag to preserve the user's intended add-type
    // (e.g. adding next_day while an instant line exists) without processCartDelivery
    // overriding the state. Flag is cleared on first skip.
    if (window._med7SkipNextProcessCart) {
      dbg('processCartDelivery SKIPPED (_med7SkipNextProcessCart=true)');
      window._med7SkipNextProcessCart = false;
      return;
    }

    const inventoryMap = buildInventoryMap();
    dbg('processCartDelivery inventoryMap keys=' + Object.keys(inventoryMap));
    // Proceed even if inventoryMap is empty — non-badge items still get next_day

    _cartDeliveryInFlight = true;
    const done = function () {
      _cartDeliveryInFlight = false;
      if (_pendingCartDelivery) {
        const pending = _pendingCartDelivery;
        _pendingCartDelivery = null;
        dbg('processCartDelivery replaying queued call preserveExisting=' + !!pending.preserveExisting);
        processCartDelivery(pending.loccodes, pending.preserveExisting);
      }
    };

    // ── Phase B: compute delivery data and save to cart_line_item_attr ─────
    // Delivery metadata is now stored in a single cart attribute (cart_line_item_attr)
    // via ONE /cart/update.js call, replacing the previous N sequential /cart/change.js
    // calls for line item property writes. Only qty-cap changes still use /cart/change.js.
    function phaseB(cart, variantTotals) {
      const items = cart.items || [];

      // Parse current cart_line_item_attr so the no-op guard can compare against it.
      const currentAttrMap = {};
      try {
        var rawAttr = cart.attributes && cart.attributes.cart_line_item_attr;
        if (rawAttr) {
          JSON.parse(rawAttr).forEach(function (e) {
            if (e.variant_id) currentAttrMap[String(e.variant_id)] = e;
          });
        }
      } catch (e) { /* ignore parse errors */ }

      // Build one group per unique variant_id (Phase A ensures one line per variant).
      // variantTotals[vid] carries the pre-merge total when Phase A removed a duplicate
      // line but hasn't yet updated the surviving line's qty in Shopify.
      const groups = {};
      items.forEach(function (item, i) {
        const vid = String(item.variant_id);
        if (!groups[vid]) {
          const mergedQty = (variantTotals && variantTotals[vid]) ? variantTotals[vid] : item.quantity;
          groups[vid] = { lineNum: i + 1, lineQty: item.quantity, totalQty: mergedQty, sku: item.sku };
        }
      });

      const deliveryDataMap = {}; // variant_id → delivery data for saveCartLineItemAttr
      const qtyUpdates      = []; // { line, quantity } for qty-cap writes (rare)
      let hasQtyChanges     = false;
      let anyChanges        = false;

      Object.keys(groups).forEach(function (vid) {
        const group = groups[vid];
        const sku   = group.sku;
        const inv   = inventoryMap[sku];
        const cur   = currentAttrMap[vid] || {}; // current delivery state from cart_line_item_attr
        const deliveryChoice = getDeliveryChoice();
        const totalQty       = group.totalQty;

        let expressQty  = 0;
        let bestLoccode = '';
        let bestLocQty  = 0;

        // Live answer wins outright when we have one — no per-loccode reduction needed,
        // bestFreeQty/bestLoccode are already the max-across-loccodes figures.
        const liveQty = getLiveExpressQty(sku);
        if (liveQty !== null) {
          expressQty  = liveQty;
          bestLoccode = getLiveBestLoccode(sku);
        } else if (inv) {
          const skuInv = (inv[sku] && typeof inv[sku] === 'object') ? inv[sku] : inv;
          loccodes.forEach(function (loccode) {
            const locQty = skuInv[loccode] !== undefined ? Number(skuInv[loccode]) : 0;
            if (locQty > bestLocQty) { bestLocQty = locQty; bestLoccode = loccode; }
          });
          expressQty = bestLocQty;
        } else {
          // Badge not in DOM — fall back to stored values in cart_line_item_attr
          // so 45_min_available is not zeroed out when the badge isn't rendered.
          expressQty  = parseInt((cur['45_min_available']) || '0', 10);
          bestLoccode = cur.quick_delivery_loccode || '';
        }

        let newType, newQty, effectiveAvail, effectiveLoccode;

        if (preserveExisting && cur.delivery_type) {
          // Cart mutation (add/remove/qty change): keep each product's current delivery_type,
          // and treat 45_min_available / quick_delivery_loccode as OWNED by whichever process
          // last established them (add-time precheck or a live-check correction) — never
          // recomputed from data-inventory here. Shopify's own qty-change section re-render
          // always resets data-inventory back to the static metafield value, discarding any
          // live correction a revalidate* function (revalidateCartOnLoad / validateCartDelivery)
          // previously patched onto the DOM. Trusting a fresh DOM re-read
          // here would silently undo that correction — or even flip delivery_type back to
          // next_day via the expressQty<=0 branch below — on every single qty +/- click.
          // Demotion/promotion of an established line is owned exclusively by the live-check
          // functions, which read the durable cart_line_item_attr instead.
          newType          = cur.delivery_type;
          effectiveAvail   = cur['45_min_available']    || '0';
          effectiveLoccode = cur.quick_delivery_loccode || '';
          const establishedAvail = parseInt(effectiveAvail, 10);
          newQty = (newType === '45_minutes')
            ? Math.min(totalQty, establishedAvail || totalQty)
            : totalQty;
        } else if (expressQty <= 0) {
          // No coverage and no established state to preserve — must be next_day
          newType = 'next_day'; newQty = totalQty;
          effectiveAvail = '0'; effectiveLoccode = '';
        } else if (!isLiveConfirmedSku(sku) && cur.delivery_type) {
          // expressQty > 0 but it came from METAFIELD data — the location API has not
          // confirmed this SKU for this location yet, so we must not claim instant off it.
          // This is the fix for "product shows next-day on its PDP but comes back INSTANT
          // once added to the cart": the PDP decided from the live response while this pass
          // decided from the metafield (via a freshly re-rendered data-inventory, or via
          // #med7-variant-inventory which is never live-corrected at all) and disagreed.
          // Default to next_day — the safe direction — and let the live-check functions
          // promote it once a real answer arrives: at the next cart-open/cart-page-load
          // (revalidateCartOnLoad) or an explicit delivery-method toggle (batchDeliveryToggle) —
          // add-to-cart itself no longer makes a live call (see CLAUDE.md).
          // 45_min_available/loccode are still recorded so that promotion has data to use.
          // `cur.delivery_type` gates this: this branch is for an ESTABLISHED line being
          // recomputed from scratch (preserveExisting=false on a location change forces
          // every line through here, even ones the branch above would otherwise have
          // carried forward unchanged) — not for a genuinely new line, see the next branch.
          dbg('phaseB variant=' + vid + ' sku=' + sku + ' — metafield says ' + expressQty +
              ' but SKU is not live-confirmed → holding next_day until the API answers');
          newType = 'next_day'; newQty = totalQty;
          effectiveAvail = String(expressQty); effectiveLoccode = bestLoccode;
        } else if (!isLiveConfirmedSku(sku)) {
          // Genuinely new to the cart — no cur.delivery_type at all, meaning this variant
          // never went through med7CartAddPrecheck's pre-write. The only way that happens is
          // a bulk/multi-item add (the `multiple`/`multipleForms` checkbox flows in
          // product-form.js), which posts {items:[...]} and therefore skips precheck
          // entirely (it only runs for a single FormData add) — so this is the very first
          // delivery-state computation this line ever gets. Trust the metafield/badge the
          // same way med7CartAddPrecheck's new-product branch does for a normal single add:
          // an express-eligible product goes instant regardless of the current cart-wide
          // choice. Without this, every bulk add of a genuinely-instant, not-yet-live-
          // confirmed product landed next_day with no correction path, since
          // revalidateAddedSku (which used to patch this up immediately after a single add)
          // was never wired to the bulk-add path either — this was a real, shipped bug.
          // The generalized live-validation trigger (see CLAUDE.md) still corrects this
          // afterward if the metafield turns out wrong.
          dbg('phaseB variant=' + vid + ' sku=' + sku + ' — new line, metafield says ' + expressQty +
              ' — trusting it (not yet live-confirmed, but nothing established to preserve)');
          newType = '45_minutes';
          newQty  = Math.min(totalQty, expressQty);
          effectiveAvail = String(expressQty); effectiveLoccode = bestLoccode;
        } else if (deliveryChoice === 'nextday') {
          // User chose next_day but coverage exists — preserve loccode for easy revert to instant
          newType = 'next_day'; newQty = totalQty;
          effectiveAvail = String(expressQty); effectiveLoccode = bestLoccode;
        } else {
          // Instant chosen: cap qty to expressQty when needed (no line splitting)
          newQty  = Math.min(totalQty, expressQty);
          newType = '45_minutes';
          effectiveAvail = String(expressQty); effectiveLoccode = bestLoccode;
        }

        dbg('phaseB variant=' + vid + ' sku=' + sku + ' choice=' + deliveryChoice + ' expressQty=' + expressQty + ' totalQty=' + totalQty + ' → newType=' + newType + ' newQty=' + newQty);

        // No-op guard: include in deliveryDataMap unchanged when already in desired state.
        // Delivery-state changes (delivery_type, loccode, 45_min_available) set anyChanges and
        // trigger a full section re-render via refreshCartDisplay.
        const hasDeliveryStateChange = (
            cur.delivery_type !== newType ||
            (cur.quick_delivery_loccode || '') !== effectiveLoccode ||
            (cur['45_min_available'] || '0') !== effectiveAvail
        );
        const hasCap = (group.lineQty !== newQty);

        if (!hasDeliveryStateChange && !hasCap) {
          dbg('phaseB variant=' + vid + ' — no-op (already in desired state)');
          deliveryDataMap[vid] = {
            delivery_type:         cur.delivery_type,
            quick_delivery_loccode: cur.quick_delivery_loccode || '',
            '45_min_available':    cur['45_min_available']   || '0'
          };
          return;
        }

        if (hasDeliveryStateChange) anyChanges = true;
        deliveryDataMap[vid] = {
          delivery_type:         newType,
          quick_delivery_loccode: effectiveLoccode,
          '45_min_available':    effectiveAvail
        };

        // Qty cap: only need a /cart/change.js call when actual line quantity changes
        if (hasCap) {
          hasQtyChanges = true;
          qtyUpdates.push({ line: group.lineNum, quantity: newQty });
        }

      });

      // Always save delivery attributes so stale keys (removed variants) get cleaned up.
      // Only skip the re-render when nothing actually changed.
      return saveCartLineItemAttr(cart, deliveryDataMap, null, 'processCartDelivery:phaseB')
        .then(function (savedCart) {
          // Clear the fallback guard set by med7CartAddPrecheck's error path.
          // In the normal path (attribute write succeeded before the add), this flag is
          // never set; clearing it here is a safe no-op in that case.
          if (window._med7PendingAddChoice) {
            window._med7PendingAddChoice = null;
            if (window._med7PendingAddChoiceTimer) {
              clearTimeout(window._med7PendingAddChoiceTimer);
              window._med7PendingAddChoiceTimer = null;
            }
          }
          if (!anyChanges && !hasQtyChanges) {
            // Attributes saved (stale keys cleaned), but no per-line badge/message change —
            // still sync parsedState + notify the widget so a removal's effect on the
            // cart-wide med7_delivery_method choice (e.g. removing the only instant-eligible
            // item) is picked up. syncChoiceFromCartItems() in cart-delivery-options.js
            // re-derives the choice from line_dt_ flags and corrects the attribute if needed.
            syncParsedStateCache(savedCart);
            done();
            updateCartDeliveryWidget();
            return;
          }
          if (hasQtyChanges) {
            // Qty caps required — apply via /cart/change.js then re-fetch for accurate totals
            return applyCartUpdates(qtyUpdates)
              .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
              .then(function (freshCart) { refreshCartDisplay(freshCart); done(); updateCartDeliveryWidget(); });
          }
          // Attribute-only change — /cart/update.js response already has updated attributes;
          // use it directly so we avoid an extra /cart.js round-trip.
          refreshCartDisplay(savedCart);
          done();
          updateCartDeliveryWidget();
        })
        .catch(function (err) {
          console.error('[ExpressDelivery] processCartDelivery phaseB failed:', err);
          done();
        });
    }

    // Use the pre-fetched cart when available (passed from deduplicateCart via scheduleCartDelivery)
    // to eliminate one /cart.js round-trip per delivery toggle / cart update cycle.
    var cartPromise = prefetchedCart
      ? Promise.resolve(prefetchedCart)
      : fetch('/cart.js').then(function (r) { return r.json(); });

    cartPromise
      .then(function (cart) {
        const items = cart.items || [];
        if (!items.length) {
          // Cart is empty — clean up any stale flat keys (line_dt_*, line_lc_*, line_am_*, etc.)
          // left from previously removed items. Without this call, orphan keys persist
          // in cart.attributes and cause syncChoiceFromCartItems to read stale 'next_day'
          // data after a cart clear, overwriting the intended 'instant' choice when the
          // next instant product is added.
          saveCartLineItemAttr(cart, {}, null, 'processCartDelivery:emptyCartCleanup').catch(function() {}).then(function() { done(); });
          return;
        }

        // ── Phase A: merge duplicate-variant lines ──────────────────────────
        // Without line splitting, each variant occupies exactly one cart line.
        // When legacy split lines exist, sum their quantities and keep only the first.
        const variantTotalQty  = {};  // vid → total qty
        const variantFirstSeen = {};  // vid → lineNum (1-based)
        const removals = [];
        items.forEach(function (item, i) {
          const vid = String(item.variant_id);
          variantTotalQty[vid] = (variantTotalQty[vid] || 0) + item.quantity;
          if (variantFirstSeen[vid] === undefined) {
            variantFirstSeen[vid] = i + 1;
          } else {
            removals.unshift(i + 1);
          }
        });

        if (removals.length) {
          const removalUpdates = removals.map(function (line) { return { line: line, quantity: 0 }; });
          return applyCartUpdates(removalUpdates)
            .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
            .then(function (cleanCart) { return phaseB(cleanCart, variantTotalQty); })
            .catch(function (err) {
              console.error('[ExpressDelivery] processCartDelivery phaseA failed:', err);
              done();
            });
        }

        return phaseB(cart, variantTotalQty);
      })
      .catch(function (err) {
        console.error('[ExpressDelivery] processCartDelivery fetch failed:', err);
        done();
      });
  }

  // ── Cart delivery API validation (live check) ────────────────────────────
  // Calls the location API directly with cart item SKUs + user lat/lng.
  // Does NOT rely on variant metafield data or badge DOM elements.
  // Sets '45_minutes' line item property when summed freeQty across all stores
  // within radius covers the full cart quantity; removes it otherwise.
  // Triggered on: checkout button click, location change, cart page load, and
  // 2s after a cart qty change settles.

  let _cartValidationInFlight = false;

  // Set to true immediately before we call refreshCartDisplay so the
  // observeCartContainers MutationObserver can skip the dedup+scheduleCartDelivery
  // re-trigger — we just wrote those properties ourselves; re-running would be a no-op
  // but costs ~700 ms (dedup fetch + 150 ms debounce + processCartDelivery fetch).
  let _ownRefresh = false;
  let _ownRefreshTimer = null;

  // Tracks whether showDeliveryLoader has injected overlays so the MutationObserver
  // knows to remove cart__items--disabled when the sections re-render completes.
  let _deliveryLoaderShown = false;
  let _deliveryLoaderTimer = null; // safety timeout handle

  // ── hideDeliveryLoader ────────────────────────────────────────────────────
  // Removes the container-level disabled class and any remaining overlay elements.
  // Called from (1) the _ownRefresh branch of observeCartContainers when sections
  // re-render completes, and (2) the safety timeout in showDeliveryLoader when a
  // re-render never fires (e.g., no delivery state changes detected by processCartDelivery).
  function hideDeliveryLoader() {
    clearTimeout(_deliveryLoaderTimer);
    _deliveryLoaderTimer = null;
    _deliveryLoaderShown = false;
    var mc = document.getElementById('main-cart-items');
    var hc = document.getElementById('header-cart');
    if (mc) mc.classList.remove('cart__items--disabled');
    if (hc) hc.classList.remove('cart__items--disabled');
    // Remove any overlay elements that survived (e.g. re-render didn't fire)
    document.querySelectorAll('.med7-delivery-loading-overlay').forEach(function (el) { el.remove(); });
  }

  // ── Single-container loading overlay ─────────────────────────────────────
  // Shows a semi-transparent overlay + spinner on the cart table as a whole
  // (#cartTable on the cart page, .js-contents-cart-items inside #header-cart
  // for the mini-cart) — one loader per visible cart container, not per line.
  // Also disables pointer events on both cart containers.
  // Called from batchDeliveryToggle (delivery method radio) and the med7:location-saved
  // handler (location change) so the user sees immediate visual feedback while
  // the async cart attribute writes + sections re-render are in-flight.
  //
  // Cleanup paths (whichever fires first):
  //   1. _ownRefresh branch in observeCartContainers — sections re-render completed.
  //   2. Safety timeout (6 s) — handles cases where processCartDelivery finds
  //      no changes and never calls refreshCartDisplay.
  function showDeliveryLoader() {
    clearTimeout(_deliveryLoaderTimer);
    _deliveryLoaderShown = true;

    // Safety timeout: force-clean the loader if no sections re-render fires within 6 s.
    _deliveryLoaderTimer = setTimeout(function () {
      if (_deliveryLoaderShown) hideDeliveryLoader();
    }, 6000);

    // Inject CSS once — uses theme's --custom-color for the spinner ring.
    if (!document.getElementById('med7-delivery-loader-style')) {
      var styleEl = document.createElement('style');
      styleEl.id = 'med7-delivery-loader-style';
      // Use explicit top/left/width/height — "inset:0" is unreliable inside
      // display:flex containers whose height is auto in some browsers.
      styleEl.textContent =
        '.med7-delivery-loading-overlay{' +
          'position:absolute;top:0;left:0;width:100%;height:100%;min-height:60px;' +
          'background:rgba(255,255,255,0.80);' +
          'z-index:999;' +
          'display:-webkit-box;display:-ms-flexbox;display:flex;' +
          '-webkit-box-align:center;-ms-flex-align:center;align-items:center;' +
          '-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;' +
          'pointer-events:none;' +
        '}' +
        '.med7-delivery-spinner{' +
          'width:24px;height:24px;' +
          'border:2.5px solid var(--custom-color,#333);' +
          'border-top-color:transparent;' +
          'border-radius:50%;' +
          '-webkit-animation:med7CartSpin .65s linear infinite;' +
          'animation:med7CartSpin .65s linear infinite;' +
        '}' +
        '@-webkit-keyframes med7CartSpin{to{-webkit-transform:rotate(360deg);transform:rotate(360deg);}}' +
        '@keyframes med7CartSpin{to{transform:rotate(360deg);}}';
      document.head.appendChild(styleEl);
    }

    // Disable all interaction on both cart containers during the update.
    document.getElementById('main-cart-items') && document.getElementById('main-cart-items').classList.add('cart__items--disabled');
    document.getElementById('header-cart') && document.getElementById('header-cart').classList.add('cart__items--disabled');

    // Inject a single overlay covering the whole cart table (cart page: #cartTable)
    // and the whole items list (mini-cart: #header-cart .js-contents-cart-items).
    // One loader per container — no per-line overlays.
    [
      document.getElementById('cartTable'),
      document.querySelector('#header-cart .js-contents-cart-items')
    ].forEach(function (container) {
      if (!container) return;
      if (container.querySelector('.med7-delivery-loading-overlay')) return; // guard re-entry
      // Ensure the container is positioned so the absolute overlay is clipped to it.
      var pos = getComputedStyle(container).position;
      if (pos === 'static') container.style.position = 'relative';
      var overlay = document.createElement('div');
      overlay.className = 'med7-delivery-loading-overlay';
      var spinner = document.createElement('div');
      spinner.className = 'med7-delivery-spinner';
      overlay.appendChild(spinner);
      container.appendChild(overlay);
    });
  }

  // Triggers cart.js's onStorageChange so it re-fetches sections and re-renders
  // the cart HTML with updated line item properties (clears any old visible properties).
  function refreshCartDisplay(cartState) {
    try {
      // Both #header-cart and #main-cart-items have separate MutationObservers. When
      // sections re-render updates both containers, two observer callbacks fire in
      // sequence. Using a timer instead of a one-shot boolean ensures ALL observer
      // fires within the window are treated as own-refresh, not just the first one.
      _ownRefresh = true;
      clearTimeout(_ownRefreshTimer);
      _ownRefreshTimer = setTimeout(function () { _ownRefresh = false; }, 500);
      localStorage.setItem('parsedState', JSON.stringify(cartState));
      localStorage.setItem('cartJs', 'true');
      window.dispatchEvent(new StorageEvent('storage', { key: 'cartJs', newValue: 'true' }));
    } catch (e) {
      console.error('[ExpressDelivery] refreshCartDisplay failed:', e);
      _ownRefresh = false;
    }
  }

  // Updates the parsedState cache after a direct DOM patch (e.g. _applyCartLineMaxes)
  // WITHOUT dispatching the storage event — no full section re-render is needed when
  // only availability/loccode attributes changed, not delivery_type itself. Keeps the
  // cache consistent so the next cart mutation's updateCartLineMaxes() read doesn't
  // revert the DOM back to the pre-correction figures.
  //
  // INVARIANT: cartState must be a /cart.js-shaped object (items[] + attributes{}).
  // localStorage.parsedState is shared with cart-delivery-options.js, which derives BOTH
  // "can this cart do instant?" and the cart-wide med7_delivery_method from it.
  // The theme breaks this invariant in several of its own paths — cart.js's
  // renderAndOpenMiniCartPopup()/renderContentsAndDependencies() and product-form.js all
  // write parsedState with either a sections-render blob ({ sections, item_count }) or a
  // raw /cart/add.js response, neither of which carries `attributes`. We cannot change
  // those, so cart-delivery-options.js treats a no-attributes parsedState as UNKNOWN and
  // re-fetches rather than reading it as "no instant lines". Never add another violation
  // from this file: pass a real cart here, or don't write parsedState at all.
  function syncParsedStateCache(cartState) {
    try {
      localStorage.setItem('parsedState', JSON.stringify(cartState));
    } catch (e) {
      console.error('[ExpressDelivery] syncParsedStateCache failed:', e);
    }
  }

  function validateCartDelivery() {
    const stored = getStoredLocation();
    if (!stored || !stored.lat || !stored.lng || !isEligibleRegion(stored)) return Promise.resolve();

    if (_cartValidationInFlight) return Promise.resolve();

    _cartValidationInFlight = true;
    const done = function () { _cartValidationInFlight = false; };

    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const items = cart.items || [];
        if (!items.length) { done(); return Promise.resolve(); }

        const skus = items.map(function (item) { return item.sku; }).filter(Boolean);
        if (!skus.length) { done(); return Promise.resolve(); }

        // getLiveAvailability resolves from the shared cache with no network call when
        // these SKUs were already confirmed for this location (e.g. by revalidateCartOnLoad
        // moments earlier on cart-open) — only genuinely-uncached SKUs hit Vinculum.
        return getLiveAvailability(skus)
          .then(function (availabilityMap) {
            // Re-fetch cart only when deduplicateCart ran concurrently during the
            // middleware network call — dedup can shift line numbers by removing
            // duplicate lines, making the original `cart` snapshot stale.
            // When no dedup was in flight the original snapshot is still accurate,
            // saving one /cart.js round-trip on every checkout / qty-change validation.
            var cartRefreshPromise = _deduplicating
              ? fetch('/cart.js').then(function (r) { return r.json(); })
              : Promise.resolve(cart);

            return cartRefreshPromise
              .then(function (currentCart) {
                if (!(currentCart.items || []).length) { done(); return Promise.resolve(); }

                return applyLiveAvailabilityToCartLines(currentCart, availabilityMap, { callerLabel: 'validateCartDelivery' })
                  .then(function (result) {
                    if (!result.changed) {
                      syncParsedStateCache(result.cart);
                      done();
                      updateCartDeliveryWidget();
                      return;
                    }
                    refreshCartDisplay(result.cart);
                    done();
                    updateCartDeliveryWidget();
                  });
              });
          });
      })
      .catch(function (err) {
        console.error('[ExpressDelivery] validateCartDelivery failed:', err);
        done();
        // Could not verify live coverage — hold the last successfully-established state
        // instead of demoting the cart. See handleLiveCheckFailure.
        handleLiveCheckFailure(err, 'validateCartDelivery');
        return Promise.resolve();
      });
  }

  // Hides every cart/mini-cart line message currently showing (whether "checking" or
  // a confirmed quantity) — called when applyNoEligibleStores() runs off the back of a
  // genuine Location API failure (or a real "no stores in range" result), so a message
  // never keeps showing "Checking availability..." once we know we can't answer that
  // question for the current location.
  function clearAllDeliveryMessages() {
    document.querySelectorAll('.express-delivery-msg[data-line]').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  // ── Non-destructive failure path for the live cart checks ────────────────────
  // Used by validateCartDelivery and revalidateCartOnLoad when their Vinculum read
  // fails outright. Deliberately does NOT call applyNoEligibleStores().
  //
  // applyNoEligibleStores() means "this location genuinely has no coverage": it wipes
  // cachedLoccodes + the med7_loccode localStorage key, force-writes
  // med7_delivery_choice = 'nextday', and rewrites EVERY cart line to next_day via
  // scheduleCartDelivery([], false). Using it from a .catch conflated "we could not get
  // an answer" with "the answer is no" — so a single 500 for one SKU Vinculum has no
  // data for demoted the customer's entire instant cart and destroyed known-good
  // location state. Because revalidateCartOnLoad runs on EVERY page load (not just
  // /cart), any transient API failure anywhere on the site did this. Real, shipped bug.
  //
  // Holding the last successfully-established state is strictly safer:
  // applyLiveAvailabilityToCartLines is demote-only AND skips SKUs with no live data, so
  // writing nothing leaves each line exactly as the last SUCCESSFUL check left it, and
  // the next successful check corrects it. Never "fail closed" to next_day here.
  function handleLiveCheckFailure(err, label) {
    dbg('live check failed (' + label + ') — holding existing delivery state:', err);
    if (err && err.isLocationApiError) showLocationApiErrorToast(err.message);
    // We cannot answer the availability question for these lines right now, so no line
    // may keep showing "Checking availability...". Lines that ARE confirmed get their
    // real text back on the next _applyCartLineMaxes pass.
    clearAllDeliveryMessages();
  }

  // ── Middleware call ────────────────────────────────────────────────────────
  // Used for initial load and location changes.
  // Collects ALL stores within the radius (no freeQty filter — qty is confirmed at checkout).

  // Demotes to next-day / clears loccodes — shared by "no stores in radius"
  // and "location outside the eligible region" (region gate never calls Vinculum).
  function applyNoEligibleStores() {
    cachedLoccodes = [];
    clearLoccodes();
    updateAllBadges([]);
    window.dispatchEvent(new CustomEvent('med7:loccodes-ready'));
    clearAllDeliveryMessages();
    scheduleCartDelivery([], false);
    // Auto-switch user preference to next_day when no store covers their location.
    // This is a SYSTEM override, not a customer decision — clear the explicit-choice
    // marker so recalculateDeliveryChoice() doesn't later mistake this forced default for
    // "the customer chose next-day" once they reach a location that does have coverage.
    if (getDeliveryChoice() !== 'nextday') {
      saveDeliveryChoice('nextday');
      clearDeliveryChoiceExplicit();
      window.dispatchEvent(new CustomEvent('med7:delivery-choice-changed', { detail: { choice: 'nextday' } }));
    } else {
      // localStorage already says nextday, but that doesn't guarantee the cart ATTRIBUTE
      // does too — e.g. a fresh/cleared cart never had med7_delivery_method set at all.
      // Check the real attribute and correct it even when there's no user-facing choice
      // change and even on an empty cart (2026-08-12). Fire-and-forget: does not affect
      // this function's synchronous return value, so every existing caller is unaffected.
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        var attrChoice = cart.attributes && cart.attributes.med7_delivery_method;
        if (attrChoice !== 'nextday') saveDeliveryChoice('nextday');
      }).catch(function () {});
    }
    return cachedLoccodes;
  }

  function callMiddleware(lat, lng, pin, skus) {
    if (!lat || !lng) {
      console.error('[ExpressDelivery] callMiddleware — missing coordinates, skipping API call');
      return Promise.resolve(null);
    }

    if (!isEligibleRegion(getStoredLocation())) {
      return Promise.resolve(applyNoEligibleStores());
    }

    // About to hit Vinculum — until it responds, PLP/PDP badges must not show a
    // (possibly stale) Express state, and cart/mini-cart show the persistent
    // loader rather than their last-known delivery type. showDeliveryLoader is a
    // no-op on pages without #cartTable/#header-cart.
    updateAllBadges([]);
    showDeliveryLoader();

    const stored = getStoredLocation();
    // Instead of the old DUMMY_SKU ('WEBSITE') placeholder, pages with no real
    // product SKU (e.g. the home page) send the region code itself as the sku.
    const resolvedSkus     = (skus && skus.length) ? skus : (window.med7ProductSkus || [(stored && stored.region_code) || '']);
    // Whether resolvedSkus are real product SKUs worth caching, vs. the region-code
    // placeholder above — priming the shared cache with a placeholder "sku" would be
    // harmless but pointless (no real SKU will ever look it up).
    const resolvedRealSkus = !!((skus && skus.length) || (window.med7ProductSkus && window.med7ProductSkus.length));
    dbg('callMiddleware: called');
    return fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY
      },
      body: JSON.stringify({
        skus: resolvedSkus,
        channelProductId: getChannelProductId(stored),
        toLatitude: lat,
        toLongitude: lng,
        toPinCode: pin,
        channelCode: CHANNEL_CODE
      })
    })
      .then(parseLocationApiResponse)
      .then(function (data) {
        dbg('callMiddleware response:', data);
        const stores = Array.isArray(data) ? data : (data.stores || data.data || []);

        // Collect all unique loccodes — no distance cap, all stores are eligible
        const loccodes = [];
        const seen = {};
        stores.forEach(function (s) {
          const loccode = String(s.loccode);
          if (!seen[loccode]) {
            seen[loccode] = true;
            loccodes.push(loccode);
          }
        });

        if (loccodes.length) {
          cachedLoccodes = loccodes;
          saveLoccodes(cachedLoccodes);
          updateAllBadges(cachedLoccodes);
          window.dispatchEvent(new CustomEvent('med7:loccodes-ready'));
          // Prime the shared per-SKU cache with this same response — when real product
          // SKUs were queried, the revalidatePDPOnLoad()/revalidateQuickViewBadge() calls
          // chained right after this resolves (see resolveLocation() and the
          // med7:location-saved handler below) become pure cache reads instead of a
          // second, near-identical Vinculum call for the SKU we just fetched.
          if (resolvedRealSkus) primeAvailabilityCache(stores);
          // Spec §4/§5: settle the cart-wide delivery choice for the new location, THEN
          // rewrite the cart. The await is load-bearing: recalculateDeliveryChoice now makes
          // a live Vinculum call, and scheduleCartDelivery → phaseB (preserveExisting=false
          // on a location change) derives EVERY line's delivery_type from
          // getDeliveryChoice(). Firing them in parallel would have phaseB read the old
          // choice and write line states the new choice then contradicts.
          // The `return` here is itself load-bearing and was missing — callMiddleware's own
          // promise used to resolve immediately after updateAllBadges()/saveLoccodes(),
          // not after this chain actually committed the recalculated choice. Every caller
          // that chains off callMiddleware (resolveLocation(), the med7:location-saved
          // handler's validateCartDelivery()/revalidatePDPOnLoad() continuation) could run
          // — and a human could click "Add to cart" — before med7_delivery_method had
          // actually been rewritten for the new location. Real, shipped bug.
          return fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) { return recalculateDeliveryChoice(cart); })
            .then(function () { scheduleCartDelivery(cachedLoccodes, false); return cachedLoccodes; })
            // recalculateDeliveryChoice never rejects, so this only covers the /cart.js fetch.
            .catch(function () { scheduleCartDelivery(cachedLoccodes, false); return cachedLoccodes; });
        }
        applyNoEligibleStores();
        return cachedLoccodes;
      })
      .catch(function (err) {
        // err is already the parsed Error from parseLocationApiResponse (that's what threw
        // it) — calling parseLocationApiResponse(err) again here tried to read .json() off
        // an Error object, threw a second, untagged TypeError, and aborted this handler
        // before either the toast or applyNoEligibleStores() below ever ran.
        dbg('callMiddleware error:', err);
        console.error('[ExpressDelivery] Middleware call failed:', err);
        if (err && err.isLocationApiError) showLocationApiErrorToast(err.message);
        // Same conservative default as "no eligible stores" — also clears the
        // showDeliveryLoader() call above via its scheduleCartDelivery→refreshCartDisplay path.
        return applyNoEligibleStores();
      });
  }

  // Locates the current PDP's own express-delivery badge (not a related-product-card
  // badge). Scoped to the Shopify section containing the main product form so
  // carousels/related-products elsewhere on the page are never mistaken for it.
  function getPDPBadge() {
    // .js-product-form is shared by the real PDP form and collection/quick-add
    // product-card forms (product-form-add-to-cart.liquid renders both). Without
    // this pathname gate, querySelector('.js-product-form') on a collection page
    // grabs the first card's form and revalidateExpressBadge wrongly live-checks that
    // random product's stock instead of no-op'ing.
    if (window.location.pathname.indexOf('/products/') === -1) return null;
    var form = document.querySelector('.js-product-form');
    if (!form) return null;
    var section = form.closest('.shopify-section, [data-section-id]') || document.body;
    return section.querySelector('.badge-express-delivery[data-sku]:not([data-cart-item])');
  }

  // Locates the currently-open Quick View modal's own express-delivery badge.
  // Quick View is loaded by AJAX into #quickView without changing window.location, so
  // it can't use getPDPBadge()'s pathname gate — it's scoped to the popup container instead.
  function getQuickViewBadge() {
    var root = document.getElementById('quickView');
    if (!root) return null;
    return root.querySelector('.badge-express-delivery[data-sku]:not([data-cart-item])');
  }

  // Hides any PDP/Quick-View message currently showing for a SKU we just failed to
  // confirm live — scoped by SKU (not "all messages") since a PDP message and a Quick
  // View message for a different product can be on screen at the same time.
  function hideExpressMsgForSku(sku) {
    document.querySelectorAll('.express-delivery-msg:not([data-line])').forEach(function(msgEl) {
      var section = msgEl.closest('.shopify-section, [data-section-id], .prd-block') || document.body;
      if (section.querySelector('.badge-express-delivery[data-sku="' + sku + '"]')) {
        msgEl.style.display = 'none';
      }
    });
  }

  // ── Live API revalidation for a single express-delivery badge ────────────────
  // Shared by revalidatePDPOnLoad() (PDP page load/variant switch/location change) and
  // revalidateQuickViewBadge() (Quick View modal open) — getBadgeFn is the only thing
  // that differs between the two call sites. The metafield-derived data-inventory is a
  // point-in-time snapshot taken whenever the theme last synced from the ERP — this
  // calls Vinculum live so the "Only N units available for 45 minute delivery" message
  // and the quantity max reflect current stock, not stale metafield data. Only ever
  // updates data-inventory (never writes delivery_type/cart attributes — there is no
  // cart line yet); updateAllBadges() then re-derives the message/qty-max from the
  // refreshed attribute exactly as it would from the metafield.
  function revalidateExpressBadge(getBadgeFn) {
    var badge = getBadgeFn();
    if (!badge) return Promise.resolve();

    var sku = badge.getAttribute('data-sku');
    if (!sku) return Promise.resolve();

    var loccodes = (cachedLoccodes && cachedLoccodes.length) ? cachedLoccodes : getStoredLoccodes();

    // getLiveAvailability resolves instantly from the shared cache when this SKU was
    // already confirmed (or is already being fetched) by another trigger — init,
    // variant-switch, quick-view-open, a prior callMiddleware for this same product,
    // etc. — without firing a second, near-identical Vinculum call.
    return getLiveAvailability([sku])
      .then(function(availabilityMap) {
        var live = availabilityMap[sku];
        if (!live) return; // no eligible location/loccodes to check against yet

        dbg('revalidateExpressBadge: refreshed live inventory for sku=' + sku);
        // Re-derives the PDP/Quick-View message, qty-max (via med7:badges-updated) and any
        // product-card badges for this SKU from the now-live data-inventory (already
        // written onto the badge DOM by primeAvailabilityCache).
        updateAllBadges(loccodes);
      })
      .catch(function(err) {
        dbg('revalidateExpressBadge error sku=' + sku + ':', err);
        if (err && err.isLocationApiError) showLocationApiErrorToast(err.message);
        // Could not confirm live availability — don't leave the message stuck on
        // "checking" forever; hide it rather than show a possibly-stale number.
        hideExpressMsgForSku(sku);
      });
  }

  function revalidatePDPOnLoad() {
    return revalidateExpressBadge(getPDPBadge);
  }

  // Called when the Quick View modal opens (see observeNewBadges() below) — live-checks
  // the Quick View's own SKU exactly as revalidatePDPOnLoad() does for the real PDP.
  function revalidateQuickViewBadge() {
    return revalidateExpressBadge(getQuickViewBadge);
  }

  // ── Cart-load live validation ──────────────────────────────────────────────
  // Called from init() with the already-fetched cart (no extra /cart.js round-trip),
  // and on every mini-cart open so a page whose initial load-time check missed a SKU
  // (or never ran one — bfcache restore, opened before location resolved, etc.) gets
  // another chance without the message staying stuck on "checking" for the rest of
  // the page's lifetime. Resolves from the shared cache whenever these SKUs were
  // already confirmed by another trigger (checkout, qty-change) at this location.
  function revalidateCartOnLoad(prefetchedCart) {
    var stored = getStoredLocation();
    if (!stored || !stored.lat || !stored.lng || !isEligibleRegion(stored)) {
      _cartMaxLoading = false;
      _setMainCartQtyLoading(false, 'revalidateCartOnLoad: no eligible location — nothing to validate');
      return Promise.resolve();
    }

    var loccodes = (cachedLoccodes && cachedLoccodes.length) ? cachedLoccodes : getStoredLoccodes();
    if (!loccodes.length) {
      _cartMaxLoading = false;
      _setMainCartQtyLoading(false, 'revalidateCartOnLoad: no cached loccodes — nothing to validate');
      return Promise.resolve();
    }

    var cartPromise = prefetchedCart
      ? Promise.resolve(prefetchedCart)
      : fetch('/cart.js').then(function(r) { return r.json(); });

    // NOTE: this `return` is load-bearing — init() chains .then(hideDeliveryLoader)
    // after this call. Without it, the chain resolves as soon as this function is
    // invoked (not when the live check actually finishes), hiding the loader and
    // making corrected values appear to "never update" once the real write lands.
    return cartPromise
      .then(function(cart) {
        var items = cart.items || [];
        if (!items.length) {
          _cartMaxLoading = false;
          _setMainCartQtyLoading(false, 'revalidateCartOnLoad: empty cart');
          return;
        }

        // Collect unique SKUs for the single (or cache-satisfied, zero-call) batch check
        var skus = [];
        var seenSkus = {};
        items.forEach(function(item) {
          if (item.sku && !seenSkus[item.sku]) { seenSkus[item.sku] = true; skus.push(item.sku); }
        });
        if (!skus.length) {
          _cartMaxLoading = false;
          _setMainCartQtyLoading(false, 'revalidateCartOnLoad: no SKUs to validate');
          return;
        }

        return getLiveAvailability(skus)
          .then(function(availabilityMap) {
            // Re-fetch cart after processCartDelivery has committed — our API call takes
            // long enough that processCartDelivery's 150 ms debounce has already run.
            // Reading a fresh cart avoids comparing against stale attributes.
            return fetch('/cart.js')
              .then(function(r) { return r.json(); })
              .then(function(freshCart) {
                return applyLiveAvailabilityToCartLines(freshCart, availabilityMap, { callerLabel: 'revalidateCartOnLoad' })
                  .then(function(result) {
                    _cartMaxLoading = false;
                    if (!result.changed) {
                      dbg('revalidateCartOnLoad: all attributes accurate');
                      // Attributes didn't need rewriting, but messages may still be showing
                      // "checking" — re-apply now that these vids are confirmed.
                      _applyCartLineMaxes(result.cart.items || [], _buildLineAttrMap(result.cart.attributes || {}));
                      // Sync a real cart snapshot even on this no-change path: the mini-cart
                      // open handler that calls us renders sections without touching
                      // parsedState, so this is the only place the cache gets refreshed —
                      // and cart-delivery-options.js needs it to re-derive the cart-wide
                      // med7_delivery_method from the surviving line_dt_* keys.
                      syncParsedStateCache(result.cart);
                      _setMainCartQtyLoading(false, 'revalidateCartOnLoad: attrs already accurate, max unchanged');
                      updateCartDeliveryWidget();
                      return;
                    }
                    if (result.typeChanged) {
                      dbg('[MED7:qtyLoader] HIDE pending re-render — type changed, _applyCartLineMaxes will clear loaders after sections re-render');
                      refreshCartDisplay(result.cart);
                      // _applyCartLineMaxes (via updateCartLineMaxes 300 ms after re-render) removes loaders
                    } else {
                      // Availability attrs corrected but type unchanged. parsedState is stale
                      // here — apply maxes directly from the updated cart so the correct max
                      // (e.g. 100 not 400) is set before removing the loader.
                      dbg('[MED7:qtyLoader] applying corrected max from updated cart before hiding loader');
                      _applyCartLineMaxes(result.cart.items || [], _buildLineAttrMap(result.cart.attributes || {}));
                      syncParsedStateCache(result.cart);
                      // _applyCartLineMaxes above already removed loaders per-item via _cartMaxLoading=false;
                      // _setMainCartQtyLoading cleans up any items that had no matching DOM input.
                      _setMainCartQtyLoading(false, 'revalidateCartOnLoad: availability corrected, max re-applied from live cart');
                      updateCartDeliveryWidget();
                    }
                  });
              });
          });
      })
      .catch(function(err) {
        // Could not verify live per-SKU coverage. Release the qty-max loader (it would
        // otherwise sit until init()'s 15 s safety timeout), then hold the existing
        // delivery state rather than demoting the whole cart — see handleLiveCheckFailure.
        _cartMaxLoading = false;
        _setMainCartQtyLoading(false, 'revalidateCartOnLoad error — holding existing delivery state');
        handleLiveCheckFailure(err, 'revalidateCartOnLoad');
      });
  }

  // ── Location resolution ────────────────────────────────────────────────────
  // Prefers coords from geolocation-setter (med7_location), falls back to GPS.

  function resolveLocation() {
    const stored = getStoredLocation();

    if (stored && stored.lat && stored.lng) {
      callMiddleware(String(stored.lat), String(stored.lng), '').then(function () {
        revalidatePDPOnLoad();
        revalidateQuickViewBadge();
      });
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          callMiddleware(String(pos.coords.latitude), String(pos.coords.longitude), '').then(function () {
            revalidatePDPOnLoad();
            revalidateQuickViewBadge();
          });
        },
        function (err) {
          // Badge stays hidden — user must set location via the geolocation drawer
          console.error('[ExpressDelivery] GPS denied:', err.message);
        }
      );
    } else {
      console.error('[ExpressDelivery] Geolocation API unavailable — badge stays hidden until user sets location');
    }
  }

  // ── Pre-checkout inventory confirmation ───────────────────────────────────
  // Intercepts checkout button clicks, calls middleware with current cart SKUs
  // to refresh inventory data and update delivery messages, then proceeds.

  // Checks each cart line item against the DOM quantity input's max attribute.
  // Caps over-stocked quantities via /cart/change.js before checkout proceeds.
  // Resolves true if any item was capped, false otherwise.
  function preCheckoutInventoryValidation() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const items = cart.items || [];
        const updates = [];

        items.forEach(function (item, i) {
          const lineNum = i + 1;
          const input = document.querySelector('[name="updates[]"][data-index="' + lineNum + '"]');
          const maxAttr = input ? input.getAttribute('max') : null;
          if (!maxAttr) return;
          const maxQty = parseInt(maxAttr, 10);
          if (!isNaN(maxQty) && maxQty >= 0 && item.quantity > maxQty) {
            updates.push({ line: lineNum, quantity: maxQty });
          }
        });

        if (!updates.length) return Promise.resolve(false);

        return updates.reduce(function (chain, update) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(update)
            }).then(function (r) { return r.json(); });
          });
        }, Promise.resolve())
          .then(function () { return true; });
      })
      .catch(function () { return false; });
  }

  // Reads cart_line_item_attr from cart attributes and writes the delivery fields
  // to each line item's properties via sequential /cart/change.js calls.
  // Called at checkout-click time so fulfilment systems see delivery metadata on
  // order line items (cart attributes become order attributes, not line properties).
  function applyLineItemPropertiesAtCheckout(prefetchedCart) {
    var cartPromise = prefetchedCart
      ? Promise.resolve(prefetchedCart)
      : fetch('/cart.js').then(function (r) { return r.json(); });
    return cartPromise
      .then(function (cart) {
        var attrs = cart.attributes || {};

        // Primary source: cart_line_item_attr JSON blob
        var attrMap = {};
        try {
          var blob = attrs.cart_line_item_attr;
          if (blob) JSON.parse(blob).forEach(function (e) {
            if (e.variant_id) attrMap[String(e.variant_id)] = e;
          });
        } catch (_) {}

        var updates = [];
        (cart.items || []).forEach(function (item, idx) {
          var vid   = String(item.variant_id);
          var entry = attrMap[vid];

          // Fallback 1: flat cart attribute keys (line_dt_/line_lc_)
          // Present even when the blob is stale or the item was added just before checkout.
          if (!entry && attrs['line_dt_' + vid]) {
            entry = {
              delivery_type:          attrs['line_dt_' + vid],
              quick_delivery_loccode: attrs['line_lc_' + vid] || ''
            };
          }

          // Fallback 2: no delivery data at all — safe next_day default.
          // Covers items added so recently that processCartDelivery hasn't run yet.
          if (!entry) {
            entry = { delivery_type: 'next_day', quick_delivery_loccode: '' };
          }

          // Properties use _ prefix so Shopify hides them from customers in checkout/emails.
          var props      = item.properties || {};
          var needsWrite =
            String(props['delivery_type']          || '') !== String(entry.delivery_type          || '') ||
            String(props['_quick_delivery_loccode'] || '') !== String(entry.quick_delivery_loccode || '');
          // TEMP TRACE (2026-08-13): remove once the missing /cart/change.js issue is confirmed fixed.
          console.error('[ExpressDelivery][TRACE] line', idx + 1, 'vid', vid, 'currentProps', props, 'desiredEntry', entry, 'needsWrite', needsWrite);
          if (!needsWrite) return;

          updates.push({
            line: idx + 1,
            properties: {
              delivery_type:          entry.delivery_type          || 'next_day',
              _quick_delivery_loccode: entry.quick_delivery_loccode || ''
            }
          });
        });

        console.error('[ExpressDelivery][TRACE] applyLineItemPropertiesAtCheckout — total updates to write:', updates.length);
        if (!updates.length) return;
        return updates.reduce(function (chain, update) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(update)
            }).then(function (r) {
              if (!r.ok) console.error('[ExpressDelivery] applyLineItemPropertiesAtCheckout: /cart/change.js failed for line ' + update.line, r.status);
            }).catch(function (err) {
              console.error('[ExpressDelivery] applyLineItemPropertiesAtCheckout: /cart/change.js request failed for line ' + update.line, err);
            });
          });
        }, Promise.resolve());
      })
      .catch(function (err) {
        console.error('[ExpressDelivery] applyLineItemPropertiesAtCheckout failed:', err);
      });
  }

  // Polls the three in-flight flags until all are clear or the deadline is reached.
  // Called at checkout-click time so the checkout chain doesn't race against a
  // processCartDelivery or validateCartDelivery that is still writing to the cart.
  function waitForCartWritesToSettle(maxWaitMs) {
    var deadline = Date.now() + (maxWaitMs || 3000);
    return new Promise(function (resolve) {
      function check() {
        if (!_cartDeliveryInFlight && !_cartValidationInFlight && !_deduplicating) {
          resolve();
        } else if (Date.now() >= deadline) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      }
      check();
    });
  }

  // ── Full-screen checkout blocking overlay ─────────────────────────────────
  // Shown the instant a checkout-triggering click is intercepted, so the customer
  // gets immediate feedback instead of an unexplained pause while the property-write
  // chain runs, and so the page can't slip into native navigation from a second
  // click/tap on another checkout entry point while we're mid-flight.
  var _checkoutOverlayEl = null;
  function showCheckoutOverlay() {
    if (_checkoutOverlayEl) return;
    if (!document.getElementById('med7-checkout-overlay-style')) {
      var styleEl = document.createElement('style');
      styleEl.id = 'med7-checkout-overlay-style';
      styleEl.textContent =
        '.med7-checkout-overlay{' +
          'position:fixed;top:0;left:0;width:100%;height:100%;' +
          'background:rgba(255,255,255,0.85);z-index:999999;' +
          'display:-webkit-box;display:-ms-flexbox;display:flex;' +
          '-webkit-box-orient:vertical;-webkit-box-direction:normal;-ms-flex-direction:column;flex-direction:column;' +
          '-webkit-box-align:center;-ms-flex-align:center;align-items:center;' +
          '-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;' +
        '}' +
        '.med7-checkout-overlay .med7-checkout-spinner-ring{' +
          'width:40px;height:40px;border-radius:50%;' +
          'border:3px solid rgba(0,0,0,0.15);border-top-color:#00647F;' +
          '-webkit-animation:med7-checkout-spin 0.8s linear infinite;animation:med7-checkout-spin 0.8s linear infinite;' +
        '}' +
        '@-webkit-keyframes med7-checkout-spin{to{-webkit-transform:rotate(360deg);transform:rotate(360deg);}}' +
        '@keyframes med7-checkout-spin{to{-webkit-transform:rotate(360deg);transform:rotate(360deg);}}';
      document.head.appendChild(styleEl);
    }
    _checkoutOverlayEl = document.createElement('div');
    _checkoutOverlayEl.className = 'med7-checkout-overlay';
    _checkoutOverlayEl.innerHTML = '<div class="med7-checkout-spinner-ring"></div>';
    document.body.appendChild(_checkoutOverlayEl);
  }
  function hideCheckoutOverlay() {
    if (_checkoutOverlayEl) { _checkoutOverlayEl.remove(); _checkoutOverlayEl = null; }
  }

  function _setCheckoutBtnLoading(btn, isLoading) {
    if (isLoading) {
      btn.disabled = true;
      var spinner = btn.querySelector('.med7-checkout-spinner');
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'loading-overlay__spinner med7-checkout-spinner';
        spinner.innerHTML = '<div data-load="loading"></div>';
        btn.insertBefore(spinner, btn.firstChild);
      } else {
        spinner.classList.remove('hidden');
      }
    } else {
      btn.disabled = false;
      var spinner = btn.querySelector('.med7-checkout-spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  // ── Checkout pre-flight helpers ───────────────────────────────────────────
  // Per-item time budget (ms) for a single /cart/change.js write.
  var CHECKOUT_T_PER_ITEM_MS = 2500;
  // Base budget (ms): covers waitForCartWritesToSettle (≤3s) + validateCartDelivery
  // middleware round-trip (~3s) + /cart.js fetches + buffer.
  var CHECKOUT_BASE_MS = 7000;

  // Counts how many cart items have mismatched or absent line item properties
  // vs. the cart attributes (blob + flat keys). Used to compute the variable timeout.
  function _countPendingCheckoutUpdates(cart) {
    var attrs = (cart && cart.attributes) || {};
    var attrMap = {};
    try {
      var blob = attrs.cart_line_item_attr;
      if (blob) JSON.parse(blob).forEach(function (e) {
        if (e.variant_id) attrMap[String(e.variant_id)] = e;
      });
    } catch (_) {}

    var count = 0;
    (cart.items || []).forEach(function (item) {
      var vid   = String(item.variant_id);
      var entry = attrMap[vid];
      if (!entry && attrs['line_dt_' + vid]) {
        entry = { delivery_type: attrs['line_dt_' + vid], quick_delivery_loccode: attrs['line_lc_' + vid] || '' };
      }
      if (!entry) { count++; return; } // no data at all → will write next_day default
      var props = item.properties || {};
      // Compare against _ prefixed keys since that is what applyLineItemPropertiesAtCheckout writes.
      if (
        String(props['delivery_type']          || '') !== String(entry.delivery_type          || '') ||
        String(props['_quick_delivery_loccode'] || '') !== String(entry.quick_delivery_loccode || '')
      ) {
        count++;
      }
    });
    return count;
  }

  // Runs the full checkout pre-flight chain (settle → validate → write properties → inventory check).
  // timedOut flag prevents double-navigation if the safety timeout fires concurrently.
  function _runCheckoutChain(btn, timeoutMs) {
    _setCheckoutBtnLoading(btn, true);
    var timedOut = false;

    // Writing delivery_type/_quick_delivery_loccode onto each line item must never be
    // skipped, even if validateCartDelivery's live middleware call runs long and the
    // safety timeout below fires first. Memoized so the normal chain and the timeout
    // branch can both await the SAME write (whichever reaches it first) instead of the
    // timeout branch skipping straight to btn.click() with nothing written.
    var writePropsPromise = null;
    function writeLineItemProperties() {
      if (!writePropsPromise) {
        writePropsPromise = fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (freshCart) { return applyLineItemPropertiesAtCheckout(freshCart); })
          .catch(function () {});
      }
      return writePropsPromise;
    }

    var timeout = setTimeout(function () {
      timedOut = true;
      writeLineItemProperties().then(function () {
        _setCheckoutBtnLoading(btn, false);
        btn.dataset.expressVerified = 'true';
        hideCheckoutOverlay();
        btn.click();
      });
    }, timeoutMs);

    waitForCartWritesToSettle(3000)
      .then(function () {
        var stored = getStoredLocation();
        return (stored && stored.lat && stored.lng)
          ? validateCartDelivery()
          : Promise.resolve();
      })
      .then(writeLineItemProperties)
      .then(function () { return preCheckoutInventoryValidation(); })
      .then(function (hadCaps) {
        if (timedOut) return;
        clearTimeout(timeout);
        _setCheckoutBtnLoading(btn, false);
        if (hadCaps) {
          hideCheckoutOverlay();
          fetch('/cart.js').then(function (r) { return r.json(); }).then(refreshCartDisplay);
          var errEl = document.getElementById('cart-errors');
          if (errEl) {
            errEl.textContent = 'Some item quantities were adjusted to match available stock. Please review your cart and proceed to checkout.';
            errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          delete btn.dataset.expressVerified;
        } else {
          btn.dataset.expressVerified = 'true';
          hideCheckoutOverlay();
          btn.click();
        }
      })
      .catch(function () {
        if (timedOut) return;
        clearTimeout(timeout);
        writeLineItemProperties().then(function () {
          _setCheckoutBtnLoading(btn, false);
          btn.dataset.expressVerified = 'true';
          hideCheckoutOverlay();
          btn.click();
        });
      });
  }

  function setupCheckoutInterception() {
    // Capture phase, on window — not document/bubble. Capture always runs top-down
    // (window before document before every descendant) before ANY bubble-phase
    // listener anywhere fires, and window's capture fires before document's capture
    // regardless of script load order. This is load-bearing: a third-party app (or
    // Shopify's own dynamic checkout button script) attaching its own bubble-phase
    // click listener directly on the checkout button — and calling stopPropagation —
    // previously meant our document-level bubble listener never saw the click at
    // all, with no error, since our code simply never ran. stopImmediatePropagation
    // below additionally stops that other listener from acting on this same click
    // once we've claimed it, without affecting any other click on the page.
    window.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="checkout"]');
      // TEMP TRACE (2026-08-13): unconditional, not DEBUG-gated — checkout clicks are
      // rare enough that this can't flood a customer console. Remove once the missing
      // /cart/change.js issue is confirmed fixed.
      console.error('[ExpressDelivery][TRACE] checkout click seen. matched btn:', !!btn, 'expressVerified:', btn && btn.dataset.expressVerified);
      if (!btn || btn.dataset.expressVerified) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showCheckoutOverlay();
      console.error('[ExpressDelivery][TRACE] interception engaged — preventDefault called, starting pre-flight fetch');

      // Pre-flight: fetch cart to count items that actually need line item property
      // writes, then set timeout = BASE + T × N so carts with fewer pending updates
      // don't wait for the worst-case budget.
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var n         = _countPendingCheckoutUpdates(cart);
          var timeoutMs = CHECKOUT_BASE_MS + CHECKOUT_T_PER_ITEM_MS * n;
          console.error('[ExpressDelivery][TRACE] pre-flight done, pending updates:', n, 'timeoutMs:', timeoutMs);
          _runCheckoutChain(btn, timeoutMs);
        })
        .catch(function (err) {
          console.error('[ExpressDelivery][TRACE] pre-flight /cart.js fetch failed, falling back to fixed timeout', err);
          // Pre-flight fetch failed — fall back to a safe fixed timeout (5-item worst case).
          _runCheckoutChain(btn, CHECKOUT_BASE_MS + CHECKOUT_T_PER_ITEM_MS * 5);
        });
    }, true);
  }

  // ── Express qty helper — sum inventory for a SKU across cached loccodes ────
  // Used by med7CartAddPrecheck to enforce the expressQty cap on instant lines.

  function readVariantInventoryMap() {
    var cartEl  = document.getElementById('med7-variant-inventory');
    var miniEl  = document.getElementById('med7-variant-inventory-mini');
    var el = cartEl || miniEl;
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent);
      dbg('invMap parsed keys:', Object.keys(parsed || {}));
      return parsed;
    } catch(e) {
      console.error('[MED7:invMap] JSON parse error:', e);
      return null;
    }
  }

  function getExpressQtyForSku(sku) {
    var loccodes = getStoredLoccodes();
    if (!loccodes.length) return 0;
    // Live answer wins — see the getLiveExpressQty doc comment. Everything below this
    // point reads metafield-derived data, which disagrees with the API often enough to
    // have caused the PDP-says-next-day / cart-says-instant mismatch.
    var liveQty = getLiveExpressQty(sku);
    if (liveQty !== null) return liveQty;
    var skuInv = null;
    var badge = document.querySelector(
      '.badge-express-delivery[data-sku="' + sku + '"][data-inventory],' +
      '.badge-next-day-delivery[data-sku="' + sku + '"][data-inventory]'
    );
    if (badge) {
      var _invAttr = badge.getAttribute('data-inventory') || '';
      if (_invAttr) {
        try {
          var inv = JSON.parse(_invAttr);
          skuInv = (sku && inv[sku] && typeof inv[sku] === 'object') ? inv[sku] : inv;
        } catch (e) {
          console.error('[MED7:getQty] badge JSON parse error:', e);
        }
      }
      // Empty data-inventory means the badge has no metafield data — fall through
      // to the readVariantInventoryMap() fallback below.
    }
    // Fallback: Liquid-serialized metafield map (covers items with no DOM badge at all)
    if (!skuInv) {
      var map = readVariantInventoryMap();
      if (map && map[sku] && typeof map[sku] === 'object') skuInv = map[sku];
    }
    if (!skuInv) return 0;
    var max = 0;
    loccodes.forEach(function (lc) { var q = Number(skuInv[lc]) || 0; if (q > max) max = q; });
    return max;
  }

  // Returns expressQty from any badge DOM element (shared by cart and card max logic).
  function getExpressQtyForBadgeEl(badgeEl) {
    if (!badgeEl) return 0;
    const loccodes = getStoredLoccodes();
    if (!loccodes.length) return 0;
    // Live answer wins over this element's data-inventory, which a section re-render
    // may have just reset back to the metafield snapshot.
    const liveQtyForEl = getLiveExpressQty(badgeEl.getAttribute('data-sku'));
    if (liveQtyForEl !== null) return liveQtyForEl;
    try {
      const inv    = JSON.parse(badgeEl.getAttribute('data-inventory'));
      const sku    = badgeEl.getAttribute('data-sku');
      const skuInv = (sku && inv[sku] && typeof inv[sku] === 'object') ? inv[sku] : inv;
      let max = 0;
      loccodes.forEach(function(lc) { const q = Number(skuInv[lc]) || 0; if (q > max) max = q; });
      return max;
    } catch(e) { return 0; }
  }

  // ── Cart line item quantity max enforcement ────────────────────────────────
  // Sets the `max` attribute on each cart line's quantity input using
  // cart_line_item_attr (the authoritative delivery metadata):
  //   45_minutes line  → max = 45_min_available (express capacity at cached loccodes)
  //   next_day line    → remove our max; Shopify's default inventory constraint applies
  // When parsedState lacks items, fetches /cart.js directly.
  function updateCartLineMaxes() {
    var ps;
    try { ps = JSON.parse(localStorage.getItem('parsedState') || 'null'); } catch(e) {}

    // Build lineAttrMap from cart_line_item_attr in parsedState when available
    var lineAttrMap = _buildLineAttrMap(ps && ps.attributes);

    if (ps && Array.isArray(ps.items)) {
      _applyCartLineMaxes(ps.items, lineAttrMap);
    } else {
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          _applyCartLineMaxes(cart.items || [], _buildLineAttrMap(cart.attributes));
        })
        .catch(function() {});
    }
  }

  // Build a { variant_id → entry } lookup map from flat cart attribute keys.
  // Key schema: line_dt_{vid}, line_lc_{vid}, line_am_{vid}
  function _buildLineAttrMap(attributes) {
    var map = {};
    var attrs = attributes || {};
    Object.keys(attrs).forEach(function(key) {
      var m = key.match(/^line_dt_(\d+)$/);
      if (!m) return;
      var vid = m[1];
      map[vid] = {
        variant_id:             vid,
        delivery_type:          attrs['line_dt_' + vid] || 'next_day',
        quick_delivery_loccode: attrs['line_lc_' + vid] || '',
        '45_min_available':     attrs['line_am_' + vid] || '0'
      };
    });
    return map;
  }

  function _applyCartLineMaxes(items, lineAttrMap) {
    lineAttrMap = lineAttrMap || {};

    items.forEach(function(item, i) {
      const lineNum  = i + 1;
      const vid      = String(item.variant_id);
      const attrEntry = lineAttrMap[vid] || {};
      // Delivery type and available qty come from cart_line_item_attr (not line properties)
      const dt        = attrEntry.delivery_type || '';
      const available = parseInt(attrEntry['45_min_available'] || '0', 10);

      const qtyInput = document.getElementById('Quantity-' + lineNum);
      if (!qtyInput) {
        dbg('[MED7:qtyMax] line ' + lineNum + ' (vid=' + vid + '): no DOM input found — skipping');
        return;
      }

      if (!qtyInput.hasAttribute('data-shopify-max')) {
        qtyInput.setAttribute('data-shopify-max', qtyInput.getAttribute('max') || '');
      }

      const qtyEl = qtyInput.closest('quantity-input');
      let maxQty = null;

      if (dt === '45_minutes' && available > 0) {
        // Cap instant-delivery lines at the express availability from the attribute
        maxQty = available;
      }
      // For next_day (or unknown): no custom max — Shopify's inventory constraint applies

      if (maxQty !== null && maxQty > 0) {
        const curVal = parseInt(qtyInput.value, 10);
        const prevMax = qtyInput.getAttribute('max');
        qtyInput.setAttribute('max', String(maxQty));
        dbg('[MED7:qtyMax] line ' + lineNum + ' (vid=' + vid + '): SET max=' + maxQty +
          ' (45_min_available=' + available + ', prev max=' + (prevMax || 'none') + ')' +
          (_cartMaxLoading ? ' [loader keeping]' : ' [loader removing]'));
        if (qtyEl) {
          qtyEl.max = String(maxQty);
          // Mirror PDP updateQtyMax: call updateStatus so the + button disabled state
          // and the visual status bar update immediately when max changes.
          if (typeof qtyEl.updateStatus === 'function') qtyEl.updateStatus(qtyInput.value);
          // Arm keyup listener so status bar tracks manual typing (once per element).
          if (!qtyEl.getAttribute('data-med7-keyup')) {
            qtyEl.setAttribute('data-med7-keyup', '1');
            (function (el, inp) {
              inp.addEventListener('keyup', function () {
                if (el.max && typeof el.updateStatus === 'function') el.updateStatus(inp.value);
              });
            })(qtyEl, qtyInput);
          }
          var qs = qtyEl.querySelector('.quantity-status');
          if (qs && !qs.classList.contains('has-animation')) {
            setTimeout(function () { qs.classList.add('has-animation'); }, 500);
          }
          var upBtn = qtyEl.querySelector('.prd-quantity-up');
          if (upBtn) {
            upBtn.setAttribute('data-tippy-content', buildQtyMaxTooltip(maxQty));
            upBtn.setAttribute('data-tippy-placement', 'top');
          }
        }
        if (!isNaN(curVal) && curVal > maxQty) {
          qtyInput.value = String(maxQty);
          if (qtyEl && typeof qtyEl.updateStatus === 'function') qtyEl.updateStatus(qtyInput.value);
        }
      } else {
        const shopifyMax = qtyInput.getAttribute('data-shopify-max') || '';
        if (shopifyMax) {
          const prevMax = qtyInput.getAttribute('max');
          qtyInput.setAttribute('max', shopifyMax);
          dbg('[MED7:qtyMax] line ' + lineNum + ' (vid=' + vid + '): RESTORE shopify max=' + shopifyMax +
            ' (dt=' + (dt || 'none') + ', available=' + available + ', prev max=' + (prevMax || 'none') + ')' +
            (_cartMaxLoading ? ' [loader keeping]' : ' [loader removing]'));
          if (qtyEl) {
            qtyEl.max = shopifyMax;
            if (typeof qtyEl.updateStatus === 'function') qtyEl.updateStatus(qtyInput.value);
            var upBtnRestore = qtyEl.querySelector('.prd-quantity-up');
            if (upBtnRestore) {
              upBtnRestore.removeAttribute('data-tippy-content');
              upBtnRestore.removeAttribute('data-tippy-placement');
            }
          }
        } else {
          const prevMax = qtyInput.getAttribute('max');
          qtyInput.removeAttribute('max');
          dbg('[MED7:qtyMax] line ' + lineNum + ' (vid=' + vid + '): REMOVE max (no shopify constraint)' +
            ' (dt=' + (dt || 'none') + ', prev max=' + (prevMax || 'none') + ')' +
            (_cartMaxLoading ? ' [loader keeping]' : ' [loader removing]'));
          if (qtyEl) {
            qtyEl.max = null;
            var qs = qtyEl.querySelector('.quantity-status');
            if (qs) qs.classList.remove('has-animation', 'qs--start', 'qs--middle', 'qs--full');
            if (qtyEl.style) qtyEl.style.removeProperty('--qty-status-persent');
            var upBtnRemove = qtyEl.querySelector('.prd-quantity-up');
            if (upBtnRemove) {
              upBtnRemove.removeAttribute('data-tippy-content');
              upBtnRemove.removeAttribute('data-tippy-placement');
            }
          }
        }
      }

      // Persistent per-line message (cart page + mini-cart), mirroring the PDP
      // static message. Uses querySelectorAll + [data-line] since the same line
      // number can be present in both the cart page and the mini-cart drawer.
      // Text is never derived from a not-yet-API-confirmed 45_min_available value
      // (that may still be processCartDelivery's metafield/badge-derived snapshot) —
      // it shows a "checking" placeholder until revalidateCartOnLoad() (cart-open/page-load)
      // confirms this variant live. Whether the message shows at all (maxQty !== null &&
      // maxQty > 0, i.e. this line is currently an eligible instant-delivery line) is
      // unaffected — only which text it shows once shown. The max attribute above is
      // also unaffected — it stays driven by maxQty exactly as before.
      document.querySelectorAll('.express-delivery-msg[data-line="' + lineNum + '"]').forEach(function (msgEl) {
        // Security check: never show the message unless the badge actually rendered in
        // this SAME cart-item row (Liquid-controlled, not JS-controlled — see
        // updateAllBadges' cart-item skip above) is the express one. maxQty/dt here come
        // from parsedState, which can be fresher than the row's last Liquid render — e.g.
        // a Scenario A correction (available/loccode patched without a full section
        // re-render) can leave the rendered badge lagging a beat behind. #CartItem-N is
        // shared by both header-cart-line-item.liquid and main-cart-items.liquid, and
        // msgEl.closest() disambiguates cart-page vs mini-cart when both are in the DOM.
        var row = msgEl.closest('.cart-table-prd, .minicart-prd');
        var lineHasExpressBadge = !!(row && row.querySelector('.badge-express-delivery[data-cart-item]'));

        if (maxQty !== null && maxQty > 0 && lineHasExpressBadge) {
          // A SKU whose live check FAILED can never become confirmed on this page load,
          // so "Checking availability..." would spin forever. Hide it instead. This
          // matters now that getLiveAvailability resolves partially: the confirmed lines
          // in a batch render their real text while the failed one must not be left
          // pending. (Distinct from "not yet checked", which legitimately shows the
          // placeholder because a result is still coming.)
          if (!confirmedExpressLineVids.has(vid) && failedAvailabilitySkus.has(item.sku)) {
            msgEl.style.display = 'none';
            return;
          }
          const msgText = confirmedExpressLineVids.has(vid) ? buildQtyMaxTooltip(maxQty) : getPDPCheckingText();
          if (msgEl.textContent !== msgText) msgEl.textContent = msgText;
          msgEl.style.display = '';
        } else {
          msgEl.style.display = 'none';
        }
      });

      // Manage loading overlay for main cart qty inputs during revalidateCartOnLoad.
      if (qtyEl) {
        if (_cartMaxLoading) {
          qtyEl.classList.add('med7-qty-loading');
        } else {
          qtyEl.classList.remove('med7-qty-loading');
        }
      }
    });
  }

  // Add or remove the med7-qty-loading class on all quantity-input elements inside
  // #main-cart-items. Called at both completion paths of revalidateCartOnLoad.
  function _setMainCartQtyLoading(loading, reason) {
    var mainCart = document.getElementById('main-cart-items');
    if (!mainCart) return;
    dbg('[MED7:qtyLoader] ' + (loading ? 'SHOW' : 'HIDE') + (reason ? ' — ' + reason : ''));
    if (loading) {
      mainCart.querySelectorAll('quantity-input').forEach(function(el) {
        el.classList.add('med7-qty-loading');
      });
    } else {
      mainCart.querySelectorAll('quantity-input.med7-qty-loading').forEach(function(el) {
        el.classList.remove('med7-qty-loading');
      });
    }
  }

  // ── Product card quantity max enforcement ──────────────────────────────────
  // Separate from PDP (pdp-delivery-options.liquid) and cart (updateCartLineMaxes).
  // Scoped to non-cart product cards via badge→form linkage:
  //   .prd-labels[data-render="badges-{id}"]  ↔  form[id="{id}"]
  // When a badge has expressQty > 0: max = expressQty.
  // When no coverage: restore Shopify's original max.
  function updateProductCardQtyMaxes() {
    const loccodes = getStoredLoccodes();

    document.querySelectorAll('.badge-express-delivery[data-inventory]').forEach(function(badge) {
      if (badge.closest('#header-cart, #main-cart-items')) return; // handled by updateCartLineMaxes

      const labelsEl = badge.closest('[data-render^="badges-"]');
      if (!labelsEl) return;
      const formId = labelsEl.getAttribute('data-render').replace('badges-', '');
      const form   = document.getElementById(formId);
      if (!form) return;
      const qtyInput = form.querySelector('[name="quantity"]');
      if (!qtyInput) return;

      if (!qtyInput.hasAttribute('data-med7-card-max')) {
        qtyInput.setAttribute('data-med7-card-max', qtyInput.getAttribute('max') || '');
      }

      const expressQty = loccodes.length ? getExpressQtyForBadgeEl(badge) : 0;
      const qtyEl      = qtyInput.closest('quantity-input');

      if (expressQty > 0) {
        qtyInput.setAttribute('max', String(expressQty));
        if (qtyEl) qtyEl.max = String(expressQty);
        const curVal = parseInt(qtyInput.value, 10);
        if (!isNaN(curVal) && curVal > expressQty) qtyInput.value = String(expressQty);
      } else {
        const shopifyMax = qtyInput.getAttribute('data-med7-card-max') || '';
        if (shopifyMax) {
          qtyInput.setAttribute('max', shopifyMax);
          if (qtyEl) qtyEl.max = shopifyMax;
        } else {
          qtyInput.removeAttribute('max');
          if (qtyEl) qtyEl.max = null;
        }
      }
    });
  }

  // ── Pre-add-to-cart intercept ─────────────────────────────────────────────
  // Called by product-form.js before posting to /cart/add.js.
  // Finds the cart line whose delivery_type matches the current user choice,
  // updates its qty (capping instant lines at expressQty), and skips the
  // duplicate add. Returns { skip: false } when normal add should proceed,
  // or { skip: true, response } when the update was handled here.

  window.med7CartAddPrecheck = function (variantId, qtyToAdd) {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items        = cart.items || [];
        var variantLines = [];
        for (var i = 0; i < items.length; i++) {
          if (items[i].variant_id === variantId) {
            variantLines.push({ lineNum: i + 1, qty: items[i].quantity,
                                sku: items[i].sku, props: items[i].properties || {} });
          }
        }
        if (!variantLines.length) {
          // ── New product: write ALL cart attributes in ONE call first, then add the line ──
          // This ensures med7_delivery_method, cart_line_item_attr, and all line_dt_/line_lc_/etc.
          // flat keys are committed BEFORE the add, so syncChoiceFromCartItems (in
          // cart-delivery-options.js) reads the correct delivery_type and does not overwrite
          // localStorage back to 'nextday' after the add completes.

          // 1. Capture PDP-add flag synchronously before any async work so it isn't
          //    cleared by a concurrent add on another variant mid-flight.
          const pdpAddPending = !!window._med7PDPAddPending;
          if (pdpAddPending) window._med7PDPAddPending = false;

          // 2. Find the badge for this product.
          //    PDP add: badge is scoped to the same Shopify section as the main product form.
          //    Card/PLP add: find the hidden input[name="id"] that matches variantId in the DOM,
          //    then walk up to the nearest card container (li / article / grid item) and look
          //    for the badge within that container. This scopes correctly even when many cards
          //    are visible on the page (unlike the old single-form querySelector approach).
          let badgeForAdd = null;
          if (pdpAddPending) {
            // PDP context — badge lives in the same section as the main product form
            const productFormEl = document.querySelector('.js-product-form');
            const sectionEl     = productFormEl ? productFormEl.closest('.shopify-section, [data-section-id]') : null;
            badgeForAdd = sectionEl ? sectionEl.querySelector('.badge-express-delivery') : null;
          } else {
            // Card/PLP context — find the card that contains this variant's id input.
            // The Lumia theme wraps each card in <product-card class="prd ..."> (a custom element).
            // 'product-card' in closest() matches the tag name, not a CSS class — so it must be
            // listed first. The fallbacks cover other card structures (li, swiper-slide, etc.).
            const variantInput = document.querySelector('[name="id"][value="' + variantId + '"]');
            const cardEl = variantInput
              ? variantInput.closest('product-card, li, article, .grid__item, .swiper-slide, [class*="product-card"], [class*="card--product"], [class*="product-item"]')
              : null;
            badgeForAdd = cardEl ? cardEl.querySelector('.badge-express-delivery') : null;
          }
          // Last-resort fallback: single badge on the entire page = unambiguous context
          if (!badgeForAdd) {
            const allBadgesForAdd = document.querySelectorAll('.badge-express-delivery');
            if (allBadgesForAdd.length === 1) badgeForAdd = allBadgesForAdd[0];
          }
          // Variant-ID fallback: covers layouts where the badge is outside the card
          // container matched by closest() — e.g. quick-view modals, alternate card
          // structures. Relies on data-variant-id added to badge-express-delivery.liquid.
          if (!badgeForAdd) {
            badgeForAdd = document.querySelector('.badge-express-delivery[data-variant-id="' + variantId + '"]');
          }

          // 3. Compute expressQty (HIGHEST single-location stock — NOT a sum) and bestLoccode
          //    from badge + cachedLoccodes. Every other reduction in the codebase takes the max
          //    (phaseB, getExpressQtyForSku, getExpressQtyForBadgeEl, batchDeliveryToggle,
          //    primeAvailabilityCache's bestFreeQty) per spec §15/§0 — a 45-minute order ships
          //    from ONE store, so summing two stores' stock is not a deliverable quantity.
          //    Summing here also mis-set 45_min_available above what a later live check
          //    would confirm, silently demoting the line at the next cart-open. Keep this
          //    identical to getExpressQtyForSku.
          // Live answer wins when available (from cache only — add-to-cart makes no live
          // call of its own, see CLAUDE.md). Otherwise fall back to the badge's own
          // data-inventory (2026-08-12) — the same metafield-derived figure the badge the
          // customer just clicked is already showing, so the cart attribute written here
          // matches what they saw. PLP SKUs never get a pre-add live check, so defaulting
          // to 0/next_day here meant almost every PLP "instant" add landed as next_day —
          // that mismatch is what this fallback fixes. Cart-open validation
          // (revalidateCartOnLoad) still corrects this afterward if the metafield turns
          // out wrong; nothing here bypasses that safety net.
          const skuForAdd = badgeForAdd ? badgeForAdd.getAttribute('data-sku') : '';
          const liveQtyAdd = getLiveExpressQty(skuForAdd);
          let expressQtyAdd = 0, bestLoccodeAdd = '';
          if (liveQtyAdd !== null) {
            expressQtyAdd  = liveQtyAdd;
            bestLoccodeAdd = getLiveBestLoccode(skuForAdd);
          } else if (badgeForAdd) {
            try {
              const invAdd    = JSON.parse(badgeForAdd.getAttribute('data-inventory') || '{}');
              const skuInvAdd = (skuForAdd && invAdd[skuForAdd] && typeof invAdd[skuForAdd] === 'object') ? invAdd[skuForAdd] : invAdd;
              getStoredLoccodes().forEach(function (loccode) {
                const locQty = Number(skuInvAdd[loccode]) || 0;
                if (locQty > expressQtyAdd) { expressQtyAdd = locQty; bestLoccodeAdd = loccode; }
              });
            } catch (e) { /* leave expressQtyAdd at 0 */ }
          }

          // 4. Determine delivery choice now that expressQtyAdd is known.
          //    PDP add: respect the explicit radio selection on the PDP.
          //    Card/PLP add: default to instant when the product is express-eligible,
          //    regardless of the current cart-wide choice — mirrors the single-line
          //    fast path ("card: instant if eligible").  For non-eligible products
          //    (expressQtyAdd = 0) preserve the cart choice so a next-day card add
          //    doesn't overwrite 'instant' for other cart items that are instant.
          const addChoice = pdpAddPending
            ? getPDPAddChoice()
            : (expressQtyAdd > 0 ? 'instant' : getDeliveryChoice());

          // 5. Compute target delivery type and capped quantity for the new line.
          //    Instant only when the cart choice is instant AND the product has express stock.
          //    Qty is capped to expressQtyAdd so the line never exceeds available instant stock.
          const targetTypeAdd = (addChoice === 'instant' && expressQtyAdd > 0) ? '45_minutes' : 'next_day';
          const newQtyAdd = (targetTypeAdd === '45_minutes') ? Math.min(qtyToAdd, expressQtyAdd) : qtyToAdd;

          // 6. Seed deliveryDataMap with ALL existing items' current data so the JSON blob
          //    remains complete and stale keys from a cleared cart are blanked automatically.
          let existingAttrArrAdd;
          try { existingAttrArrAdd = JSON.parse((cart.attributes || {}).cart_line_item_attr || '[]'); } catch(e) { existingAttrArrAdd = []; }
          const deliveryDataMapAdd = {};
          existingAttrArrAdd.forEach(function(entry) {
            const evid = String(entry.variant_id || '');
            if (evid) deliveryDataMapAdd[evid] = entry;
          });

          // 7. Add the new product's computed delivery entry
          deliveryDataMapAdd[String(variantId)] = {
            delivery_type:          targetTypeAdd,
            quick_delivery_loccode: bestLoccodeAdd,
            '45_min_available':     String(expressQtyAdd)
          };

          // 8. Build a fake cart that includes the new item so saveCartLineItemAttr:
          //    (a) writes line_dt_{newVid} and the JSON blob entry for the new product
          //    (b) automatically blanks stale line_*_{old_vid} keys (handles cleared-cart case)
          const fakeNewItem = { variant_id: variantId, quantity: newQtyAdd, title: '' };
          const fakeCartForAttr = {
            items:      items.concat([fakeNewItem]),
            attributes: cart.attributes || {}
          };

          // 9. Build sections list for the /cart/add.js response (mirrors fast-path logic)
          const cartItemsElAdd = document.querySelector('cart-items');
          const sectionsAdd = cartItemsElAdd
            ? Array.from(new Set(
                cartItemsElAdd.getSectionsToRender()
                  .filter(function (item) { return Boolean(document.querySelector('#' + item.id)); })
                  .map(function (s) { return s.section; })
              )).filter(Boolean).slice(0, 5)
            : ['header-cart'];

          // 10. Write ALL attributes in ONE /cart/update.js call, THEN do the add.
          //    product-form.js awaits precheck, so the attribute write is guaranteed to
          //    complete before Shopify processes the add request.
          //    The .catch below guards ONLY the saveCartLineItemAttr call; /cart/add.js
          //    errors are intentionally propagated to product-form.js for its error UI.
          // PROMOTE-ONLY on the cart-wide choice: flip med7_delivery_method to 'instant'
          // when this new line is instant, otherwise leave the existing choice untouched.
          // The cart-wide value means "at least one line is instant" — exactly what
          // syncChoiceFromCartItems (cart-delivery-options.js) derives from the line_dt_*
          // keys — so writing the OLD choice here was self-inconsistent: it left
          // med7_delivery_method on 'nextday' while line_dt_{new} said 45_minutes, which
          // (a) rendered the widget with next-day preselected and (b) fed a stale 'nextday'
          // into getDeliveryChoice() for every reader that followed. Never demote here: a
          // next-day add must not drag an existing instant cart down with it.
          // Other lines are unaffected — phaseB's `preserveExisting && cur.delivery_type`
          // guard carries every established line's own delivery_type forward unchanged.
          // The single-line fast path and merge path below already write `choice` the same way.
          const cartChoiceAfterAdd = (targetTypeAdd === '45_minutes') ? 'instant' : getDeliveryChoice();
          // Keep localStorage in step with the attribute in the SAME breath. phaseB reads
          // getDeliveryChoice() moments later on the next cart mutation; leaving it stale is
          // what let a just-added instant line get demoted before anything could render it.
          try { localStorage.setItem(DELIVERY_CHOICE_KEY, cartChoiceAfterAdd); } catch (e) {}
          return saveCartLineItemAttr(fakeCartForAttr, deliveryDataMapAdd, { med7_delivery_method: cartChoiceAfterAdd }, 'med7CartAddPrecheck:newProduct')
            .catch(function() {
              // Attribute write failed — set fallback guard so syncChoiceFromCartItems
              // cannot overwrite localStorage, then signal fallback via null sentinel.
              window._med7PendingAddChoice = addChoice;
              if (window._med7PendingAddChoiceTimer) clearTimeout(window._med7PendingAddChoiceTimer);
              window._med7PendingAddChoiceTimer = setTimeout(function() {
                window._med7PendingAddChoice = null;
                window._med7PendingAddChoiceTimer = null;
              }, 10000);
              return null; // sentinel: attribute write failed — fall back to normal add
            })
            .then(function(attrResult) {
              if (attrResult === null) return { skip: false }; // attribute write failed
              // Guard syncChoiceFromCartItems (cart-delivery-options.js) against overwriting
              // localStorage back to 'nextday' while processCartDelivery is still running.
              // product-form.js calls setParsedState() right after renderContents(), which
              // overwrites parsedState with the /cart/add.js response (no line_dt_ keys).
              // syncChoiceFromCartItems then sees no instant entries and saves 'nextday',
              // causing processCartDelivery to set the newly-added instant product to next_day.
              // Setting _med7PendingAddChoice here (success path) tells syncChoiceFromCartItems
              // to skip — mirroring what the error path already does.
              // Cleared by processCartDelivery phaseB or by the safety timeout below.
              window._med7PendingAddChoice = addChoice;
              if (window._med7PendingAddChoiceTimer) clearTimeout(window._med7PendingAddChoiceTimer);
              window._med7PendingAddChoiceTimer = setTimeout(function() {
                window._med7PendingAddChoice = null;
                window._med7PendingAddChoiceTimer = null;
              }, 5000);
              // localStorage was already written above (promote-only), in the same breath as
              // the med7_delivery_method attribute — the two must never disagree.
              // NOTE: if /cart/add.js fails after attributes are written (e.g. inventory
              // exhausted between precheck and add), attributes will briefly reference a
              // product not yet in the cart. This is transient — processCartDelivery's next
              // run prunes the orphan keys when it finds no matching cart item.
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: variantId,
                  quantity: newQtyAdd,
                  sections: sectionsAdd,
                  sections_url: window.location.pathname
                })
              })
              .then(function(r) { return r.json(); })
              .then(function(addResponse) {
                // First-time add already exceeded the express max and was silently
                // capped above (newQtyAdd) — surface that instead of adding quietly.
                if (targetTypeAdd === '45_minutes' && expressQtyAdd > 0 && newQtyAdd < qtyToAdd && !addResponse.status) {
                  return { skip: true, response: { status: 422, description: buildQtyMaxTooltip(expressQtyAdd) } };
                }

                // The add itself failed (e.g. inventory exhausted between precheck and
                // add) — nothing was created.
                if (addResponse.status) {
                  return { skip: true, response: addResponse };
                }

                // Return skip:true so product-form.js renders the sections response.
                return { skip: true, response: addResponse };
              });
            });
        }

        var sku        = variantLines[0].sku;
        var expressQty = getExpressQtyForSku(sku);

        // Read the existing cart_line_item_attr first so the choice below can respect
        // a delivery type already established for this line (must happen BEFORE choice
        // is computed — see curDeliveryType usage right below).
        //   (a) detect whether the delivery_type for this variant actually needs to change
        //   (b) build a patched deliveryDataMap for the explicit saveCartLineItemAttr call
        // We seed deliveryDataMap with ALL existing variants so saveCartLineItemAttr
        // writes a complete, accurate JSON blob (not just the one being updated).
        var existingAttrArr;
        try { existingAttrArr = JSON.parse((cart.attributes || {}).cart_line_item_attr || '[]'); } catch(e) { existingAttrArr = []; }
        var deliveryDataMap = {};
        var curDeliveryType = 'next_day';
        existingAttrArr.forEach(function(entry) {
          var evid = String(entry.variant_id || '');
          if (evid) {
            deliveryDataMap[evid] = entry;
            if (evid === String(variantId)) curDeliveryType = entry.delivery_type || 'next_day';
          }
        });

        // PDP adds set _med7PDPAddPending before calling precheck; card adds do not.
        // For card adds on a brand-new-to-instant product, default to instant when
        // eligible so cards add to the fastest available delivery type. But when the
        // line ALREADY exists in the cart on next_day (e.g. user just switched the
        // cart-wide delivery method to next-day), respect that instead of silently
        // flipping it back to instant and re-applying the express cap.
        var choice;
        if (window._med7PDPAddPending) {
          choice = getPDPAddChoice();  // respect the explicit PDP radio choice
          window._med7PDPAddPending = false;
        } else if (curDeliveryType === 'next_day') {
          choice = 'nextday';  // existing line already on next-day: keep it
        } else {
          choice = expressQty > 0 ? 'instant' : 'nextday';  // card: instant if eligible
        }
        var targetType = (choice === 'instant' && expressQty > 0) ? '45_minutes' : 'next_day';

        // Compute merged total across ALL variant lines regardless of delivery type.
        // This handles cross-type merges (e.g. 7×next_day + adding 6×instant)
        // so processCartDelivery never sees a mismatched multi-line state that
        // would set hasQtyChanges=true and trigger the refreshCartDisplay loop.
        var totalExisting = 0;
        variantLines.forEach(function (l) { totalExisting += l.qty; });
        var mergedTotal = totalExisting + qtyToAdd;

        // Cap instant lines at expressQty. Track whether the user's actual intended
        // total (before capping) exceeded the express max, so the cap can be
        // surfaced to the user instead of silently applied (see expressCapped below).
        var expressCapped = false;
        if (targetType === '45_minutes' && expressQty > 0) {
          if (mergedTotal > expressQty) expressCapped = true;
          mergedTotal = Math.min(mergedTotal, expressQty);
        }

        // Build the sections list the same way CartItems does
        var cartItemsEl = document.querySelector('cart-items');
        var sections = cartItemsEl
          ? Array.from(new Set(
              cartItemsEl.getSectionsToRender()
                .filter(function (item) { return Boolean(document.querySelector('#' + item.id)); })
                .map(function (s) { return s.section; })
            )).filter(Boolean).slice(0, 5)
          : ['header-cart'];

        // ── Fast path: single line ──────────────────────────────────────────────
        if (variantLines.length === 1) {
          var existingQty = variantLines[0].qty;
          // Only skip when qty AND delivery type are both unchanged.
          // When instant cap causes mergedTotal < existingQty AND the delivery type
          // needs to change (e.g. 7×next_day → 5×instant), we must proceed so both
          // qty and delivery_type are updated atomically.
          if (mergedTotal <= existingQty && targetType === curDeliveryType) {
            // The line is already at (or the add would have pushed it past) the express
            // max — surface the cap instead of silently letting the normal /cart/add.js
            // call proceed uncapped (which would round-trip through Shopify and later
            // get corrected back down silently by validateCartDelivery).
            if (expressCapped) {
              return { skip: true, response: { status: 422, description: buildQtyMaxTooltip(expressQty) } };
            }
            return { skip: false };
          }

          // ── Pre-write attributes when delivery type changes ──────────────────────
          // Write ALL cart attributes BEFORE /cart/change.js so the sections Shopify
          // renders at change time already contain the correct line_dt_{vid} value
          // and therefore the correct Liquid-rendered badge.  Without this, the
          // changeResponse sections are stale (badge still shows 'next_day') and
          // product-form.js renders the wrong badge; refreshCartDisplay fires later
          // but races with setCartJs(), leading to a flash or missed update.
          var prewritePromise = Promise.resolve();
          if (targetType !== curDeliveryType) {
            // bestLoccode from the live answer — expressQty above already comes from
            // getExpressQtyForSku (live-first), so deriving the loccode from the metafield
            // could name a store that doesn't correspond to the quantity being written.
            var newLocFast = (targetType === '45_minutes') ? getLiveBestLoccode(sku) : '';
            // Seed the map with the new delivery state.
            deliveryDataMap[String(variantId)] = {
              delivery_type:          targetType,
              quick_delivery_loccode: newLocFast,
              '45_min_available':     String(expressQty)
            };
            try { localStorage.setItem(DELIVERY_CHOICE_KEY, choice); } catch(e) {}
            prewritePromise = saveCartLineItemAttr(cart, deliveryDataMap, { med7_delivery_method: choice }, 'med7CartAddPrecheck:singleLine:prewrite')
              .then(function() {
                // Re-assert localStorage in case initDeliveryWidget ran during the
                // /cart/update.js round-trip and overwrote the choice from stale
                // Liquid sections (the sections are generated before our prewrite).
                try { localStorage.setItem(DELIVERY_CHOICE_KEY, choice); } catch(e) {}
              })
              .catch(function() {}); // pre-write failure is non-fatal; proceed with change
          }

          return prewritePromise
            .then(function() {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  line: variantLines[0].lineNum, quantity: mergedTotal,
                  sections: sections, sections_url: window.location.pathname
                })
              });
            })
            .then(function(r) { return r.json(); })
            .then(function(changeResponse) {
              if (changeResponse.errors) return { skip: false };
              var actualQty     = mergedTotal;
              var responseItems = changeResponse.items || [];
              for (var k = 0; k < responseItems.length; k++) {
                if (responseItems[k].variant_id === variantId) { actualQty = responseItems[k].quantity; break; }
              }

              if (actualQty < mergedTotal) {
                var addedCount = Math.max(0, actualQty - existingQty);
                var desc = addedCount > 0
                  ? 'Only ' + addedCount + ' item' + (addedCount !== 1 ? 's were' : ' was') + ' added to cart due to availability'
                  : 'All available items for this product are already in your cart';
                return { skip: true, response: { status: 422, description: desc } };
              }

              // Our own express cap reduced the user's intended total (mergedTotal was
              // already capped before this request) — Shopify itself didn't further
              // reduce it, so the check above never fires. Surface the express cap here.
              if (expressCapped) {
                return { skip: true, response: { status: 422, description: buildQtyMaxTooltip(expressQty) } };
              }
              return { skip: true, response: changeResponse };
            });
        }

        // ── Merge path: multiple lines for the same variant (legacy split lines) ──
        // Keep the first line, remove the rest, then update the surviving line's qty.
        // Delivery metadata is NOT written here — processCartDelivery will update
        // cart_line_item_attr on the next cart DOM mutation.
        var primaryLine = variantLines[0];

        // Remove non-primary lines in descending order so earlier line numbers stay valid
        var linesToRemove = variantLines
          .filter(function (l) { return l !== primaryLine; })
          .map(function (l) { return l.lineNum; })
          .sort(function (a, b) { return b - a; });

        var removeChain = linesToRemove.reduce(function (chain, lineNum) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: lineNum, quantity: 0 })
            }).then(function (r) { return r.json(); });
          });
        }, Promise.resolve());

        return removeChain
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(function (updatedCart) {
            var updatedItems   = updatedCart.items || [];
            var updatedLineNum = -1;
            for (var k = 0; k < updatedItems.length; k++) {
              if (updatedItems[k].variant_id === variantId) {
                updatedLineNum = k + 1;
                break;
              }
            }
            if (updatedLineNum === -1) return { skip: false }; // variant vanished — fallback

            // Update qty on the surviving line. If delivery type also needs to change,
            // write it atomically below (same pattern as the single-line fast path).
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                line: updatedLineNum, quantity: mergedTotal,
                sections: sections, sections_url: window.location.pathname
              })
            })
              .then(function (r) { return r.json(); })
              .then(function (changeResponse) {
                if (changeResponse.errors) return { skip: false };

                var actualQty     = mergedTotal;
                var responseItems = changeResponse.items || [];
                for (var k = 0; k < responseItems.length; k++) {
                  if (responseItems[k].variant_id === variantId) {
                    actualQty = responseItems[k].quantity; break;
                  }
                }

                // Write delivery type when it differs from what's in cart_line_item_attr.
                // Same race-safe pattern as single-line fast path: atomic /cart/update.js
                // with med7_delivery_method included, then re-assert localStorage, then
                // scheduleCartDelivery to update all items and trigger one re-render.
                if (targetType !== curDeliveryType) {
                  // Live-derived loccode, same reasoning as the fast path above.
                  var newLocM = (targetType === '45_minutes') ? getLiveBestLoccode(sku) : '';
                  deliveryDataMap[String(variantId)] = {
                    delivery_type:          targetType,
                    quick_delivery_loccode: newLocM,
                    '45_min_available':     String(expressQty)
                  };
                  try { localStorage.setItem(DELIVERY_CHOICE_KEY, choice); } catch(e) {}
                  saveCartLineItemAttr(changeResponse, deliveryDataMap, { med7_delivery_method: choice }, 'med7CartAddPrecheck:mergePath')
                    .then(function(updatedCart) {
                      try { localStorage.setItem(DELIVERY_CHOICE_KEY, choice); } catch(e) {}
                      refreshCartDisplay(updatedCart);
                      scheduleCartDelivery(cachedLoccodes, false, updatedCart);
                    });
                }

                if (actualQty < mergedTotal) {
                  var addedCount = Math.max(0, actualQty - totalExisting);
                  var desc = addedCount > 0
                    ? 'Only ' + addedCount + ' item' + (addedCount !== 1 ? 's were' : ' was') + ' added to cart due to availability'
                    : 'All available items for this product are already in your cart';
                  return { skip: true, response: { status: 422, description: desc } };
                }
                return { skip: true, response: changeResponse };
              });
          })
          .catch(function () { return { skip: false }; });
      })
      .catch(function () { return { skip: false }; });
  };

  // ── Cart refresh after add-to-cart error ─────────────────────────────────────
  // Called by product-form.js when a cart add returns an error (e.g. inventory
  // cap 422). Uses CartItems.getSectionsRendered() to fetch fresh section HTML
  // and re-render directly, bypassing the storage-event path whose window.event
  // access is unreliable across browsers (notably Firefox).

  window.med7DeduplicateAndRefreshCart = function () {
    var cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof cartItemsEl.getSectionsRendered === 'function') {
      cartItemsEl.getSectionsRendered()
        .then(function (response) {
          if (!response) return;
          cartItemsEl.renderContents(response);
          if (typeof cartItemsEl.toggleEmptyClass === 'function') cartItemsEl.toggleEmptyClass(response);
          if (typeof cartItemsEl.setCartJs       === 'function') cartItemsEl.setCartJs();
          // Sync a real cart snapshot rather than cartItemsEl.setParsedState(response) —
          // `response` here is { sections, item_count } with no items/attributes, and
          // parsedState must stay cart-shaped for cart-delivery-options.js (see the
          // invariant note on syncParsedStateCache).
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(syncParsedStateCache)
            .catch(function () {});
        })
        .catch(function () {
          fetch('/cart.js').then(function (r) { return r.json(); }).then(refreshCartDisplay).catch(function () {});
        });
    } else {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(refreshCartDisplay).catch(function () {});
    }
  };

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    cachedLoccodes = getStoredLoccodes();

    // Single /cart.js fetch shared by syncDeliveryChoiceFromCart, badge init, and
    // cart-page delivery pass — replaces 2-3 independent /cart.js calls at page load.
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Spec §2: sync delivery choice from cart attribute (server-side source of truth).
        var cartAttr = cart.attributes && cart.attributes.med7_delivery_method;
        if (cartAttr === 'instant' || cartAttr === 'nextday') {
          try { localStorage.setItem(DELIVERY_CHOICE_KEY, cartAttr); } catch (e) {}
        }

        const badges = document.querySelectorAll('.badge-express-delivery');
        if (badges.length) {
          if (cachedLoccodes.length) {
            updateAllBadges(cachedLoccodes);
            // Pass the already-fetched cart so processCartDelivery skips its own fetch.
            scheduleCartDelivery(cachedLoccodes, true, cart);
          } else {
            resolveLocation();
          }
        }

        // PDP: refresh this product's badge with a live Vinculum call on every load.
        // No-op on non-PDP pages (getPDPBadge() finds nothing) and on cart-item badges.
        revalidatePDPOnLoad();

        // On the cart page, run a live delivery check and a delivery pass.
        // preserveExisting=true so delivery types set at add time are not overwritten
        // on page load; only coverage loss or a user-triggered method change alters them.
        // revalidateCartOnLoad alone now covers what used to be a validateCartDelivery →
        // revalidateCartOnLoad chain (two sequential middleware calls) — its demote check
        // folds in validateCartDelivery's must-not-promote / choice-aware rule, so cart-page
        // load only needs one round trip to Vinculum.
        if (window.location.pathname === '/cart') {
          _cartMaxLoading = true;
          _setMainCartQtyLoading(true, 'cart page load — waiting for revalidateCartOnLoad live API');
          // Safety: clear loader after 15 s in case revalidateCartOnLoad bails early (no location/loccodes).
          setTimeout(function() {
            if (_cartMaxLoading) {
              _cartMaxLoading = false;
              _setMainCartQtyLoading(false, 'safety timeout — revalidateCartOnLoad did not complete in 15s');
            }
          }, 15000);
          // Persistent cart loader spans the whole live-check chain — but only when we'll
          // actually call Vinculum; an ineligible location never does, so it never waits.
          var cartPageEligible = isEligibleRegion(getStoredLocation());
          if (cartPageEligible) showDeliveryLoader();
          revalidateCartOnLoad(cart)
            .then(function() { if (cartPageEligible) hideDeliveryLoader(); });
          scheduleCartDelivery(cachedLoccodes, true, cart);
        } else {
          // Non-cart pages: still mask the mini-cart badge (Liquid-rendered from the last
          // known cart attribute) while the live check runs, whenever a location with
          // potential coverage is already known — same loader showDeliveryLoader() already
          // uses for #header-cart on the cart page, just not gated on being on /cart.
          var miniCartLoaderShown = cachedLoccodes.length > 0;
          if (miniCartLoaderShown) showDeliveryLoader();
          revalidateCartOnLoad(cart)
            .then(function() { if (miniCartLoaderShown) hideDeliveryLoader(); });
        }
      })
      .catch(function () {
        // Fallback: proceed without pre-fetched cart; each path fetches independently.
        const badges = document.querySelectorAll('.badge-express-delivery');
        if (badges.length) {
          if (cachedLoccodes.length) {
            updateAllBadges(cachedLoccodes);
            scheduleCartDelivery(cachedLoccodes, true);
          } else {
            resolveLocation();
          }
        }

        revalidatePDPOnLoad();

        if (window.location.pathname === '/cart') {
          _cartMaxLoading = true;
          _setMainCartQtyLoading(true, 'cart page load (catch fallback) — waiting for revalidateCartOnLoad live API');
          setTimeout(function() {
            if (_cartMaxLoading) {
              _cartMaxLoading = false;
              _setMainCartQtyLoading(false, 'safety timeout (catch path) — revalidateCartOnLoad did not complete in 15s');
            }
          }, 15000);
          var cartPageEligibleFallback = isEligibleRegion(getStoredLocation());
          if (cartPageEligibleFallback) showDeliveryLoader();
          revalidateCartOnLoad()
            .then(function() { if (cartPageEligibleFallback) hideDeliveryLoader(); });
          scheduleCartDelivery(cachedLoccodes, true);
        } else {
          var miniCartLoaderShownFallback = cachedLoccodes.length > 0;
          if (miniCartLoaderShownFallback) showDeliveryLoader();
          revalidateCartOnLoad()
            .then(function() { if (miniCartLoaderShownFallback) hideDeliveryLoader(); });
        }
      });
  }

  // ── Variant switch: MutationObserver (after productRenderElements replaces badge HTML) ──

  function observeBadgeContainers() {
    const containers = document.querySelectorAll('[data-render^="badges-"], .prd-labels');
    if (!containers.length) return;

    containers.forEach(function (container) {
      new MutationObserver(function () {
        updateAllBadges(cachedLoccodes);
        // Variant switch may have swapped in a new SKU's badge with a metafield-only
        // data-inventory — revalidatePDPOnLoad() no-ops if this SKU is already confirmed/pending.
        revalidatePDPOnLoad();
      }).observe(container, { childList: true, subtree: true });
    });
  }

  // ── Cart update: re-evaluate badges and delivery types after AJAX re-render ─
  // cart.js replaces .js-contents-cart-items in both mini-cart and main cart
  // on every add/remove/quantity change. New badge elements start hidden.

  let _cartUpdateTimer = null;
  function observeCartContainers() {
    const selectors = [
      '#header-cart .js-contents-cart-items',
      '#main-cart-items .js-contents-cart-items'
    ];
    selectors.forEach(function (sel) {
      const container = document.querySelector(sel);
      if (!container) return;

      new MutationObserver(function () {
        // Re-apply badge visibility and line max attributes on every cart DOM change,
        // regardless of source — this is cheap and always correct.
        updateAllBadges(cachedLoccodes);
        clearTimeout(_cartMaxTimer);
        _cartMaxTimer = setTimeout(updateCartLineMaxes, 300); // reduced from 1000 ms

        // When the DOM change was triggered by our own refreshCartDisplay call we
        // already wrote the correct delivery properties — running dedup+schedule
        // again would be a pure no-op but costs ~700 ms of network round-trips.
        if (_ownRefresh) {
          dbg('observeCartContainers: own-refresh — skipping dedup+schedule');
          // Sections re-render replaced .js-contents-cart-items innerHTML, so the
          // per-item overlay elements are already gone. Just clean up the container-level
          // disabled class (and cancel the safety timeout via hideDeliveryLoader).
          if (_deliveryLoaderShown) {
            hideDeliveryLoader();
          }
          return;
        }

        // showDeliveryLoader() injects a spinner div into #header-cart .js-contents-cart-items,
        // which fires this observer. The _batchToggleInFlight flag is set synchronously in
        // med7SetDeliveryChoice before batchDeliveryToggle (and thus showDeliveryLoader) runs.
        // MutationObserver callbacks are always async, so the flag is already true here.
        // Skipping dedup+schedule prevents processCartDelivery from fetching stale cart data
        // and overwriting the correct next_day attributes batchDeliveryToggle is about to write.
        if (window._med7BatchToggleInFlight) {
          dbg('observeCartContainers: batchDeliveryToggle in flight — skipping dedup+schedule');
          return;
        }

        clearTimeout(_cartUpdateTimer);
        _cartUpdateTimer = setTimeout(function () {
          dbg('observeCartContainers fired → deduplicateCart + scheduleCartDelivery');
          // deduplicateCart passes its freshest cart snapshot into the callback so
          // scheduleCartDelivery → processCartDelivery can skip their own /cart.js fetch.
          deduplicateCart(function (latestCart) {
            scheduleCartDelivery(cachedLoccodes, true, latestCart);
          });
        }, 100);

        // Qty change was pending — DOM is now updated; wait 2 s then validate live.
        // Also fires for ANY cart mutation while the cart is actually being viewed —
        // on the /cart page itself (e.g. an add from an embedded "frequently bought
        // together"/recommended-products form) or with the mini-cart drawer already
        // open (there is no future "open" click left to trigger it otherwise, since
        // add-to-cart no longer auto-opens the drawer — see CLAUDE.md). Safe from the
        // self-triggering loop a naive version of this would cause: every corrective
        // refreshCartDisplay() call sets _ownRefresh first, and the early-return above
        // already skips this whole block whenever that flag is set.
        var miniCartOpenEl = document.querySelector('.dropdn-modal-minicart');
        var cartIsBeingViewed = document.body.classList.contains('template-cart') ||
          !!(miniCartOpenEl && miniCartOpenEl.classList.contains('is-opened'));
        if (_qtyChangePending || cartIsBeingViewed) {
          clearTimeout(_validateCartTimer);
          _validateCartTimer = setTimeout(function () {
            _qtyChangePending = false;
            validateCartDelivery();
          }, 2000);
        }
      }).observe(container, { childList: true, subtree: true });
    });
  }

  // ── Qty +/- button listener — arms the validate debounce ─────────────────

  function setupQtyChangeListener() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.prd-quantity-btn');
      if (!btn) return;
      // Only intercept inside a cart container, not PDP quantity buttons
      if (!btn.closest('#header-cart, #main-cart-items')) return;

      _qtyChangePending = true;
    });
  }

  // ── Global badge observer — re-evaluates badges whenever new badge elements appear ──
  // Replaces the old grid-only observePlpGrid(). A single document.body subtree observer
  // covers PLP pagination, all carousels, recently-viewed sections, and any future
  // product card placement without needing per-container wiring.
  // The callback short-circuits immediately if no .badge-express-delivery node is found
  // among added nodes, keeping overhead negligible for unrelated DOM mutations.

  function observeNewBadges() {
    let _newBadgeTimer = null;

    new MutationObserver(function (mutations) {
      let hasBadge = false;
      for (let i = 0; i < mutations.length; i++) {
        const nodes = mutations[i].addedNodes;
        for (let j = 0; j < nodes.length; j++) {
          const node = nodes[j];
          if (node.nodeType !== 1) continue;
          if (node.classList.contains('badge-express-delivery') ||
              node.querySelector('.badge-express-delivery')) {
            hasBadge = true;
            break;
          }
        }
        if (hasBadge) break;
      }
      if (!hasBadge) return;

      clearTimeout(_newBadgeTimer);
      _newBadgeTimer = setTimeout(function () {
        updateAllBadges(cachedLoccodes);
        // Fires on every Quick View open (its inner HTML is torn down on close and
        // rebuilt on open, so a fresh .badge-express-delivery node lands here every
        // time) — a no-op via revalidateExpressBadge's confirmed/pending guard if
        // this SKU was already checked, or if no Quick View is currently open at all.
        revalidateQuickViewBadge();
      }, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Fallback for variant change when badges container isn't data-rendered
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches('input[name="id"]')) {
      setTimeout(function () { updateAllBadges(cachedLoccodes); revalidatePDPOnLoad(); }, 350);
    }
  });

  // ── React to geolocation-setter confirming a new location ─────────────────

  window.addEventListener('med7:location-saved', function (e) {
    const detail = e.detail || {};
    clearLoccodes();
    cachedLoccodes = [];
    // revalidateExpressBadge's/revalidateCartOnLoad's confirmed data (and the shared
    // liveAvailabilityCache backing all of them) is scoped to the location it was
    // fetched for (Vinculum filters by lat/lng) — a location change invalidates it
    // even though the SKU/variant is unchanged, so PDP, Quick View and cart/mini-cart
    // messages must all go back to "checking".
    confirmedExpressSkus.clear();
    confirmedExpressLineVids.clear();
    clearAvailabilityCache();
    clearLiveInventoryMarkers();
    // Show per-item loaders immediately — cart attributes and line items will update
    // once callMiddleware + processCartDelivery complete; the loader prevents interaction
    // in the meantime and cleans up when sections re-render (or after 6 s safety timeout).
    showDeliveryLoader();
    if (detail.lat && detail.lng) {
      callMiddleware(String(detail.lat), String(detail.lng), '').then(function () {
        validateCartDelivery();
        revalidatePDPOnLoad();
        revalidateQuickViewBadge();
      });
    } else {
      resolveLocation();
    }
  });

  // ── Location reset helper ──────────────────────────────────────────────────

  window.med7ClearExpressLocation = function () {
    clearLoccodes();
    cachedLoccodes = [];
    confirmedExpressSkus.clear();
    confirmedExpressLineVids.clear();
    clearAvailabilityCache();
    clearLiveInventoryMarkers();
    resolveLocation();
  };

  // ── Batch delivery toggle ────────────────────────────────────────────────────
  // Updates BOTH the med7_delivery_method cart attribute AND cart_line_item_attr
  // in a single /cart/update.js POST (replacing the previous flow that issued
  // N parallel /cart/change.js line-property writes after the attribute write).
  //   1. GET /cart.js  → read current cart + cart_line_item_attr state
  //   2. Compute delivery data for every variant (same logic as phaseB)
  //   3. ONE POST /cart/update.js  → writes med7_delivery_method + cart_line_item_attr
  //   4. refreshCartDisplay with the /cart/update.js response (no extra fetch needed)
  //   5. Only call /cart/change.js when a qty cap is needed (rare: qty > expressQty)
  function batchDeliveryToggle(choice) {
    // Show per-item loaders immediately — no need to wait for the /cart.js fetch.
    showDeliveryLoader();

    var inventoryMap = buildInventoryMap(); // build once at toggle time

    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];

        // Parse current cart_line_item_attr for no-op guards
        var currentAttrMap = {};
        try {
          var rawAttr = cart.attributes && cart.attributes.cart_line_item_attr;
          if (rawAttr) {
            JSON.parse(rawAttr).forEach(function (e) {
              if (e.variant_id) currentAttrMap[String(e.variant_id)] = e;
            });
          }
        } catch (e) { /* ignore */ }

        var deliveryDataMap = {};
        var qtyUpdates      = [];
        var hasQtyChanges   = false;
        var _debugRows      = []; // populated below for pre-update.js console.table

        items.forEach(function (item, idx) {
          var lineNum = idx + 1;
          var sku     = item.sku;
          var vid     = String(item.variant_id);
          var cur     = currentAttrMap[vid] || {};
          var inv     = inventoryMap[sku];

          // Mirror phaseB: find the loccode with highest inventory among cached loccodes
          var expressQty  = 0;
          var bestLoccode = '';
          var bestLocQty  = 0;
          var liveSku      = liveAvailabilityCache[sku];
          var usedLiveData = false;

          if (liveSku) {
            // Prefer the live-confirmed number over the (possibly stale) metafield
            // snapshot — by the time a user touches this radio, revalidateCartOnLoad/
            // validateCartDelivery have almost always already confirmed these SKUs for
            // this location, so this is normally a zero-network-call read.
            expressQty     = liveSku.bestFreeQty || 0;
            bestLoccode    = liveSku.bestLoccode || '';
            usedLiveData   = true;
            confirmedExpressLineVids.add(vid);
          } else if (inv) {
            var skuInv = (inv[sku] && typeof inv[sku] === 'object') ? inv[sku] : inv;
            cachedLoccodes.forEach(function (loccode) {
              var locQty = skuInv[loccode] !== undefined ? Number(skuInv[loccode]) : 0;
              if (locQty > bestLocQty) { bestLocQty = locQty; bestLoccode = loccode; }
            });
            expressQty = bestLocQty;
          } else {
            // No live data and no badge in DOM — fall back to existing cart_line_item_attr values
            expressQty  = parseInt(cur['45_min_available'] || '0', 10);
            bestLoccode = cur.quick_delivery_loccode || '';
          }

          var newType, newLoc, newQty;
          if (expressQty <= 0) {
            newType = 'next_day'; newLoc = ''; newQty = item.quantity;
          } else if (choice === 'nextday') {
            newType = 'next_day'; newLoc = bestLoccode; newQty = item.quantity;
          } else {
            newQty  = Math.min(item.quantity, expressQty);
            newType = '45_minutes'; newLoc = bestLoccode;
          }

          deliveryDataMap[vid] = {
            delivery_type:         newType,
            quick_delivery_loccode: newLoc,
            '45_min_available':    String(expressQty)
          };

          if (item.quantity !== newQty) {
            hasQtyChanges = true;
            qtyUpdates.push({ line: lineNum, quantity: newQty });
          }

          // Build debug row explaining the decision for this item
          var _reason;
          if (usedLiveData) {
            _reason = 'live-confirmed expressQty=' + expressQty + ' at ' + bestLoccode + (item.quantity > expressQty ? ' (qty ' + item.quantity + ' capped to ' + newQty + ')' : '');
          } else if (!inv) {
            _reason = expressQty > 0
              ? 'badge NOT in DOM; fell back to stored 45_min_available=' + expressQty + ', loccode=' + bestLoccode
              : 'badge NOT in DOM + no stored 45_min_available → expressQty=0 → forced next_day';
          } else if (expressQty <= 0) {
            _reason = 'badge in DOM but expressQty=0 across loccodes ' + JSON.stringify(cachedLoccodes) + ' → forced next_day';
          } else if (choice === 'nextday') {
            _reason = 'user chose nextday; expressQty=' + expressQty + ' exists at ' + bestLoccode + ' but instant not requested';
          } else {
            _reason = 'choice=instant + expressQty=' + expressQty + ' at ' + bestLoccode + (item.quantity > expressQty ? ' (qty ' + item.quantity + ' capped to ' + newQty + ')' : '');
          }
          _debugRows.push({
            sku:            sku,
            variant_id:     vid,
            line:           lineNum,
            qty:            item.quantity,
            prev_type:      cur.delivery_type || '(none)',
            new_type:       newType,
            expressQty:     expressQty,
            loccode:        newLoc,
            reason:         _reason
          });
        });

        // ── Pre-update.js diagnostic log ────────────────────────────────────────
        if (DEBUG) {
          console.group('[MED7:batchDeliveryToggle] About to POST /cart/update.js');
          console.log('Trigger: delivery method radio changed → "' + choice + '"');
          console.log('Cached loccodes:', JSON.stringify(cachedLoccodes));
          console.table(_debugRows);
          if (hasQtyChanges) console.log('Qty cap updates (will follow as /cart/change.js):', qtyUpdates);
          console.groupEnd();
        }
        // ────────────────────────────────────────────────────────────────────────

        // Apply max immediately to the current DOM inputs so the + button disabled state
        // and status bar update without waiting for the sections re-render.
        // Mirror of PDP updateQtyMax: deliveryDataMap has the same shape as lineAttrMap.
        // The post-render updateCartLineMaxes call (300 ms after MutationObserver) handles
        // the newly-rendered inputs that replace these after refreshCartDisplay.
        _applyCartLineMaxes(items, deliveryDataMap);

        // Single /cart/update.js call: med7_delivery_method + cart_line_item_attr together
        return saveCartLineItemAttr(cart, deliveryDataMap, { med7_delivery_method: choice }, 'batchDeliveryToggle')
          .then(function (savedCart) {
            if (hasQtyChanges) {
              // Qty caps needed — apply sequentially then re-fetch for accurate totals
              return applyCartUpdates(qtyUpdates)
                .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
                .then(function (freshCart) {
                  refreshCartDisplay(freshCart);
                });
            }
            // savedCart from /cart/update.js already has updated attributes — use directly
            refreshCartDisplay(savedCart);
          });
      });
  }

  // ── Delivery choice API — called by cart-delivery-options.js widget ──────────

  // Guards against concurrent batchDeliveryToggle calls (e.g. label/radio double-fire
  // where the browser synthesises a second click event 10–50 ms after the first).
  // Exposed on window so cart-delivery-options.js can skip the redundant cartJs storage
  // listener handling while the toggle cycle is already in flight (Fix B).
  let _batchToggleInFlight = false;

  window.med7SetDeliveryChoice = function (choice) {
    if (_batchToggleInFlight) {
      dbg('med7SetDeliveryChoice: ignored — batchDeliveryToggle already in flight (choice=' + choice + ')');
      return;
    }
    dbg('med7SetDeliveryChoice choice=' + choice);
    // Save to localStorage only — batchDeliveryToggle handles the cart attribute write
    // via /cart/update.js so we avoid the duplicate fire-and-forget in saveDeliveryChoice.
    try { localStorage.setItem(DELIVERY_CHOICE_KEY, choice); } catch (e) {}
    // The one and only caller of med7SetDeliveryChoice is the cart/mini-cart radio's own
    // click handler — a genuine customer decision, unlike every other writer of
    // DELIVERY_CHOICE_KEY in this file (all automatic/system defaults).
    markDeliveryChoiceExplicit();
    _batchToggleInFlight = true;
    window._med7BatchToggleInFlight = true;
    batchDeliveryToggle(choice)
      .then(function () {
        _batchToggleInFlight = false;
        window._med7BatchToggleInFlight = false;
        window.dispatchEvent(new CustomEvent('med7:delivery-choice-changed', { detail: { choice: choice } }));
      })
      .catch(function (err) {
        _batchToggleInFlight = false;
        window._med7BatchToggleInFlight = false;
        dbg('batchDeliveryToggle failed, falling back to processCartDelivery:', err);
        saveDeliveryChoice(choice);
        processCartDelivery(cachedLoccodes);
        window.dispatchEvent(new CustomEvent('med7:delivery-choice-changed', { detail: { choice: choice } }));
      });
  };

  // Returns current delivery state for the widget's initial read
  window.med7GetDeliveryState = function () {
    return {
      choice: getDeliveryChoice(),
      canInstant: cachedLoccodes.length > 0
    };
  };

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      observeBadgeContainers();
      observeCartContainers();
      observeNewBadges();
      setupCheckoutInterception();
      setupQtyChangeListener();
    });
  } else {
    init();
    observeBadgeContainers();
    observeCartContainers();
    observeNewBadges();
    setupCheckoutInterception();
    setupQtyChangeListener();
  }

  // Mini-cart open refresh: cart badges are Liquid-rendered and only reflect the
  // server-side line_dt_ attribute state at the time Shopify rendered the section.
  // On bfcache restores (browser back button) or after delivery-type changes that
  // happened on another page, the frozen mini-cart HTML may show stale next_day
  // badges. Whenever the user clicks the cart icon, re-fetch the header-cart section
  // from Shopify and swap the DOM before they see the stale content.
  var _miniCartRefreshInFlight = false;
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-src="#dropdnMinicartPopup"]')) return;
    var cartItemsEl = document.getElementById('header-cart');
    if (!cartItemsEl || typeof cartItemsEl.getSectionsRendered !== 'function') return;
    if (_miniCartRefreshInFlight) return;
    _miniCartRefreshInFlight = true;
    var miniCartOpenLoaderShown = cachedLoccodes.length > 0;
    cartItemsEl.getSectionsRendered()
      .then(function (response) {
        cartItemsEl.renderContents(response);
        cartItemsEl.toggleEmptyClass(response);
        // No setParsedState(response) — that would replace the cart snapshot in
        // parsedState with a { sections, item_count } blob and blind the delivery widget
        // (see syncParsedStateCache's invariant note). revalidateCartOnLoad() below
        // fetches /cart.js and syncs a real snapshot instead.
        // The fresh Liquid render above reflects the last-confirmed attribute, not the
        // live check that's about to run — mask it the same way the cart page does,
        // called in this same tick (before the browser paints) so there's no flash.
        if (miniCartOpenLoaderShown) showDeliveryLoader();
      })
      .catch(function () {})
      .then(function () {
        _miniCartRefreshInFlight = false;
        // The section re-render above only swaps in Liquid-rendered HTML — it never
        // hits the live Vinculum API, so any line whose SKU wasn't confirmed at the
        // page's initial revalidateCartOnLoad() run (e.g. location/loccodes not ready
        // yet at that point) is stuck showing the "checking" placeholder forever,
        // since confirmedExpressLineVids is only populated by a live API response.
        // Re-run the live check on every cart open so a previously-unconfirmed line
        // gets a real chance to resolve.
        revalidateCartOnLoad()
          .then(function() { if (miniCartOpenLoaderShown) hideDeliveryLoader(); });
      });
  });
})();
